<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AiCloserAPI from 'dashboard/api/aiCloser';
import {
  loadRepairBundleRuns,
  saveRepairBundleRun,
  summarizeRepairBundleConfidence,
} from '../../lib/repairBundleConfidence';
import {
  buildSavedBatchTestSetLibraryExport,
  findSavedBatchTestSet,
  formatSavedBatchTestSetInput,
  mergeSavedBatchTestSets,
  normalizeSavedBatchTestSetLibraryImport,
  normalizeSavedBatchTestSets,
  parseBatchSimulationInput,
  summarizeBatchSimulationResult,
  summarizeBatchSimulationRows,
} from '../../lib/batchSimulationSet';
import {
  advanceRollbackSessionContext,
  buildOperatorNoteTimelineBadges,
  buildOperatorNoteTimelineVersionDialogContext,
  buildRollbackEvidenceCompareBundle,
  buildRollbackBreadcrumbBadges,
  buildOperatorNoteTimelineContextLabel,
  ensureRollbackSessionContext,
  findBatchBaseline,
  findReleaseReviewSnapshot,
  mergeOperatorNoteTimelineEntries,
  mergeBatchBaselines,
  normalizeRollbackSessionContext,
  rollbackSessionContextFromEntry,
} from '../../lib/releaseLoopSnapshots';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  initialView: {
    type: String,
    default: 'preview',
  },
});

const store = useStore();
const alert = useAlert();
const route = useRoute();
const router = useRouter();

const isReloading = ref(false);
const isSimulating = ref(false);
const isRunningBatch = ref(false);
const isPersistingRollbackFollowup = ref(false);

const query = ref('');
const selectedInboxId = ref('');
const batchInput = ref('');
const batchSetTitleDraft = ref('');
const selectedSavedBatchSetKey = ref('');

const simulateResult = ref(null);
const repairValidationRuns = ref([]);
const batchSimulationRows = ref([]);
const releaseReadinessSummary = ref({});
const savedBatchTestSetsData = ref([]);
const savedTestSetImportInputRef = ref(null);
const isImportingSavedSets = ref(false);
const isExportingSavedSets = ref(false);

const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const products = computed(() => store.getters['aiCloser/getProducts'] || []);
const connectedInboxes = computed(
  () => store.getters['ailabInboxes/getRecords'] || []
);
const inboxById = computed(() => store.getters['inboxes/getInbox']);
const promptSections = computed(
  () => aiCloserConfig.value?.prompt_sections || {}
);
const currentReleaseLoop = computed(() => promptSections.value?.release_loop || {});
const currentReleaseSnapshots = computed(
  () =>
    releaseReadinessSummary.value?.release_snapshots?.length
      ? releaseReadinessSummary.value.release_snapshots
      : currentReleaseLoop.value?.review_snapshots || []
);
const currentBatchBaselines = computed(
  () =>
    releaseReadinessSummary.value?.batch_baselines?.length
      ? releaseReadinessSummary.value.batch_baselines
      : currentReleaseLoop.value?.batch_baselines || []
);
const savedBatchTestSets = computed(() =>
  normalizeSavedBatchTestSets(
    savedBatchTestSetsData.value.length
      ? savedBatchTestSetsData.value
      : currentReleaseLoop.value?.saved_test_sets
  )
);
const currentSavedBatchTestSet = computed(() =>
  findSavedBatchTestSet({
    sets: savedBatchTestSets.value,
    setKey: selectedSavedBatchSetKey.value,
  })
);
const publishedVersionId = computed(() => {
  const num = Number(
    releaseReadinessSummary.value?.published_version_id ??
      aiCloserConfig.value?.published_version_id
  );
  return Number.isFinite(num) && num > 0 ? num : null;
});
const draftVersionId = computed(() => {
  const num = Number(
    releaseReadinessSummary.value?.draft_version_id ??
      aiCloserConfig.value?.draft_version_id
  );
  return Number.isFinite(num) && num > 0 ? num : null;
});
const hasUnpublishedVersion = computed(
  () => draftVersionId.value && draftVersionId.value !== publishedVersionId.value
);

const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);

const pageTitle = computed(() => 'Test workspace');

const pageDescription = computed(() => 'Run the real simulation flow and inspect the response.');
const CONTENT_SEED_QUERY_KEYS = [
  'content_seed',
  'content_seed_source',
  'content_seed_focus',
  'content_seed_query',
  'content_seed_reason',
  'content_seed_scope_warning',
];
const GUIDANCE_SEED_QUERY_KEYS = [
  'guidance_seed',
  'guidance_seed_source',
  'guidance_seed_editor',
  'guidance_seed_reason',
  'guidance_seed_query',
];
const DEPLOY_SEED_QUERY_KEYS = [
  'deploy_seed',
  'deploy_seed_source',
  'deploy_seed_focus',
  'deploy_seed_reason',
  'deploy_seed_inbox_id',
  'deploy_seed_confidence_level',
  'deploy_seed_confidence_runs',
  'deploy_seed_confidence_title',
  'deploy_seed_batch_baseline_key',
  'deploy_seed_batch_set_key',
  'deploy_seed_batch_title',
  'deploy_seed_batch_overall_level',
  'deploy_seed_batch_total_cases',
  'deploy_seed_batch_ready_cases',
  'deploy_seed_batch_watch_cases',
  'deploy_seed_batch_blocked_cases',
  'deploy_seed_batch_ran_at',
  'deploy_seed_batch_top_blocker',
  'deploy_seed_batch_top_watch_signal',
  'deploy_seed_followup_intent',
  'deploy_seed_target_version_label',
  'deploy_seed_compare_version_label',
  'deploy_seed_prefill_note',
  'deploy_seed_open_versions',
  'deploy_seed_session_id',
  'deploy_seed_session_label',
  'deploy_seed_session_step',
  'deploy_seed_session_origin',
  'deploy_seed_session_item_key',
];
const DEPLOY_COMPARE_QUERY_KEYS = [
  'deploy_seed_bundle',
  'deploy_seed_title',
  'deploy_seed_focus_label',
  'deploy_seed_strategy_group_title',
  'deploy_seed_recommendation_code',
  'deploy_seed_owner_workspace',
  'deploy_seed_owner_action_title',
  'deploy_seed_guidance_editor',
  'deploy_seed_escalation_focus',
  'deploy_seed_hotspot_summary',
  'deploy_seed_resolved_summary',
  'deploy_seed_escalated_summary',
  'deploy_seed_validation_summary',
  'deploy_seed_stage',
  'deploy_seed_rollback_trigger',
];
const ESCALATION_SEED_QUERY_KEYS = [
  'escalation_seed',
  'escalation_seed_source',
  'escalation_seed_focus',
  'escalation_seed_reason',
  'escalation_seed_query',
  'escalation_seed_label',
];
const TEST_SEED_QUERY_KEYS = [
  'test_query',
  'test_inbox_id',
  'test_reason',
  'test_source',
];
const TEST_COMPARE_QUERY_KEYS = [
  'test_compare_bundle',
  'test_compare_title',
  'test_compare_focus_label',
  'test_compare_strategy_group_title',
  'test_compare_recommendation_code',
  'test_compare_owner_workspace',
  'test_compare_owner_action_title',
  'test_compare_guidance_editor',
  'test_compare_escalation_focus',
  'test_compare_hotspot_summary',
  'test_compare_resolved_summary',
  'test_compare_escalated_summary',
  'test_compare_validation_summary',
  'test_compare_stage',
];
const TEST_FOLLOWUP_QUERY_KEYS = [
  'test_followup_intent',
  'test_followup_target_version_label',
  'test_followup_compare_version_label',
  'test_followup_session_id',
  'test_followup_session_label',
  'test_followup_session_step',
  'test_followup_session_origin',
  'test_followup_session_item_key',
];
const seededTestContext = computed(() => ({
  query: String(route.query?.test_query || '').trim(),
  inboxId: String(route.query?.test_inbox_id || '').trim(),
  reason: String(route.query?.test_reason || '').trim(),
  source: String(route.query?.test_source || '').trim(),
}));
const seededRollbackFollowup = computed(() => ({
  intent: String(route.query?.test_followup_intent || '').trim(),
  targetVersionLabel: String(route.query?.test_followup_target_version_label || '').trim(),
  compareVersionLabel: String(route.query?.test_followup_compare_version_label || '').trim(),
}));
const readTestRouteRollbackSession = () =>
  normalizeRollbackSessionContext({
    sessionId: route.query?.test_followup_session_id,
    sessionLabel: route.query?.test_followup_session_label,
    sessionStep: route.query?.test_followup_session_step,
    sessionOrigin: route.query?.test_followup_session_origin,
  });
const currentTestRollbackSessionItemKey = () =>
  String(
    route.query?.test_followup_session_item_key || route.query?.rollback_session_item_key || ''
  ).trim();
const currentTestRollbackSession = () => {
  const routeSession = readTestRouteRollbackSession();
  if (routeSession.sessionId) return routeSession;
  const historySession = rollbackSessionContextFromEntry(
    rollbackEvidenceHistoryEntries.value?.[0] || null
  );
  return historySession.sessionId ? historySession : normalizeRollbackSessionContext();
};
const ensureTestRollbackSession = nextWorkspace =>
  ensureRollbackSessionContext({
    current: currentTestRollbackSession(),
    sourceWorkspace: 'Test',
    nextWorkspace,
  });
const advanceTestRollbackSession = nextWorkspace =>
  advanceRollbackSessionContext({
    current: currentTestRollbackSession(),
    sourceWorkspace: 'Test',
    nextWorkspace,
  });
const buildRollbackFollowupCompareBundle = outcome => ({
  title: String(outcome?.bundle_title || outcome?.bundle_label || '').trim(),
  focusLabel: String(outcome?.focus_label || '').trim(),
  strategyGroupTitle: '',
  recommendationCode: String(outcome?.recommendation_code || '').trim(),
  ownerWorkspace: 'test',
  ownerActionTitle: String(
    outcome?.operator_actions?.[0]?.intent_label ||
      outcome?.recommended_action_label ||
      'Rollback follow-up'
  ).trim(),
  guidanceEditor: '',
  escalationFocus: '',
  hotspotSummary: '',
  resolvedSummary: '',
  escalatedSummary: '',
  validationSummary:
    outcome?.target_version_label && outcome?.previous_version_label
      ? `${outcome.target_version_label} vs ${outcome.previous_version_label}`
      : '',
  stage: 'validation',
});
const buildRollbackFollowupNote = outcome =>
  [
    String(outcome?.summary || '').trim(),
    String(outcome?.operator_note || '').trim(),
    String(outcome?.recommended_action_label || '').trim(),
    outcome?.target_version_label
      ? `${outcome.target_version_label}${outcome?.previous_version_label ? ` vs ${outcome.previous_version_label}` : ''}`
      : '',
  ]
    .filter(Boolean)
    .join(' • ');
const buildRollbackFollowupHistoryEntry = outcome => ({
  ...ensureTestRollbackSession('Deploy'),
  kind: 'rollback_followup',
  hopKind: 'evidence_note',
  actorActionLabel: 'Persist rollback follow-up',
  workspaceLabel: 'Test',
  body: buildRollbackFollowupNote(outcome),
  source: 'Test',
  targetVersionLabel: String(outcome?.target_version_label || '').trim(),
  compareVersionLabel: String(
    outcome?.compare_version_label || outcome?.previous_version_label || ''
  ).trim(),
  recommendedActionLabel: String(outcome?.recommended_action_label || '').trim(),
  confidenceLabel: String(outcome?.confidence_label || '').trim(),
  nextWorkspace: 'Deploy',
});
const findRollbackEvidenceTargets = outcome => {
  const snapshotId = Number(outcome?.release_snapshot_id);
  const batchBaselineId = Number(outcome?.batch_baseline_id);
  const snapshot =
    currentReleaseSnapshots.value.find(
      row => Number(row?.id) > 0 && Number(row.id) === snapshotId
    ) ||
    findReleaseReviewSnapshot({
      snapshots: currentReleaseSnapshots.value,
      bundle: buildRollbackFollowupCompareBundle(outcome),
    }) ||
    null;
  const baseline =
    currentBatchBaselines.value.find(
      row => Number(row?.id) > 0 && Number(row.id) === batchBaselineId
    ) ||
    findBatchBaseline({
      baselines: currentBatchBaselines.value,
      baselineKey: String(outcome?.batch_baseline_key || '').trim(),
      setKey: String(outcome?.batch_set_key || '').trim(),
      bundle: buildRollbackFollowupCompareBundle(outcome),
    }) ||
    null;
  return { snapshot, baseline };
};
const persistRollbackFollowupToEvidence = async () => {
  if (isPersistingRollbackFollowup.value) return;
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!props.assistant?.id || !outcome) return;

  const note = buildRollbackFollowupNote(outcome);
  if (!note) {
    alert('No rollback follow-up note is available to persist yet');
    return;
  }

  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  const payload = {
    appendOperatorNoteEntry: buildRollbackFollowupHistoryEntry(outcome),
  };
  const writes = [];
  if (Number(snapshot?.id) > 0) {
    writes.push(AiCloserAPI.updateReleaseSnapshot(props.assistant.id, snapshot.id, payload));
  }
  if (Number(baseline?.id) > 0) {
    writes.push(AiCloserAPI.updateBatchBaseline(props.assistant.id, baseline.id, payload));
  }
  if (!writes.length) {
    alert('No linked release snapshot or batch baseline is available for this rollback outcome');
    return;
  }

  isPersistingRollbackFollowup.value = true;
  try {
    await Promise.all(writes);
    await refreshReleaseReadinessSummary();
    alert(
      writes.length > 1
        ? 'Rollback follow-up appended to the linked snapshot and batch baseline'
        : snapshot?.id
          ? 'Rollback follow-up appended to the linked release snapshot'
          : 'Rollback follow-up appended to the linked batch baseline'
    );
  } catch (error) {
    alert(
      error?.response?.data?.errors?.join(', ') ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save the rollback follow-up note'
    );
  } finally {
    isPersistingRollbackFollowup.value = false;
  }
};
const seededRepairBundle = computed(() => {
  const title = String(route.query?.test_compare_title || '').trim();
  const focusLabel = String(route.query?.test_compare_focus_label || '').trim();
  const strategyGroupTitle = String(
    route.query?.test_compare_strategy_group_title || ''
  ).trim();
  const recommendationCode = String(
    route.query?.test_compare_recommendation_code || ''
  ).trim();
  const ownerWorkspace = String(
    route.query?.test_compare_owner_workspace || ''
  ).trim();
  const ownerActionTitle = String(
    route.query?.test_compare_owner_action_title || ''
  ).trim();
  const guidanceEditor = String(
    route.query?.test_compare_guidance_editor || ''
  ).trim();
  const escalationFocus = String(
    route.query?.test_compare_escalation_focus || ''
  ).trim();
  const hotspotSummary = String(
    route.query?.test_compare_hotspot_summary || ''
  ).trim();
  const resolvedSummary = String(
    route.query?.test_compare_resolved_summary || ''
  ).trim();
  const escalatedSummary = String(
    route.query?.test_compare_escalated_summary || ''
  ).trim();
  const validationSummary = String(
    route.query?.test_compare_validation_summary || ''
  ).trim();
  const stage = String(route.query?.test_compare_stage || '').trim();
  return {
    enabled:
      String(route.query?.test_compare_bundle || '').trim() === '1' ||
      Boolean(
        title ||
          hotspotSummary ||
          resolvedSummary ||
          escalatedSummary ||
          validationSummary
      ),
    title,
    focusLabel,
    strategyGroupTitle,
    recommendationCode,
    ownerWorkspace,
    ownerActionTitle,
    guidanceEditor,
    escalationFocus,
    hotspotSummary,
    resolvedSummary,
    escalatedSummary,
    validationSummary,
    stage,
  };
});
const currentTestQuery = computed(() => String(query.value || seededTestContext.value.query || '').trim());
const analyzeSeedContext = computed(() => ({
  inboxId: String(selectedInboxId.value || seededTestContext.value.inboxId || '').trim(),
  contentGapReason: String(debugTrace.value.content_gap_reason || '').trim(),
  articleScopeWarning: String(debugTrace.value.article_scope_warning || '').trim(),
  deliveryMode:
    traceDeliveryMode.value && traceDeliveryMode.value !== '—'
      ? String(traceDeliveryMode.value).trim()
      : '',
}));
const parsedBatchCases = computed(() =>
  parseBatchSimulationInput({
    input: batchInput.value,
    defaultInboxId: selectedInboxId.value || seededTestContext.value.inboxId || '',
  })
);
const batchSimulationSummary = computed(() =>
  summarizeBatchSimulationRows(batchSimulationRows.value)
);

const summaryCards = computed(() => [
  { label: 'Status', value: aiCloserConfig.value?.status || 'unknown' },
  { label: 'Prompt sections', value: Object.keys(promptSections.value || {}).length },
  { label: 'Guidance cards', value: (Array.isArray(promptSections.value.guardrails) ? promptSections.value.guardrails.length : 0) + (Array.isArray(promptSections.value.response_guidelines) ? promptSections.value.response_guidelines.length : 0) + (Array.isArray(promptSections.value.scenarios) ? promptSections.value.scenarios.length : 0) },
  {
    label: 'Procedures',
    value: Object.keys(promptSections.value?.slot_schema?.intents || {}).length,
  },
  { label: 'Products', value: products.value.length },
  { label: 'Mode', value: isAIGamer.value ? 'ai_gamer' : 'ai_closer' },
]);

const simulationMessages = computed(() => {
  const plan = Array.isArray(simulateResult.value?.send_plan)
    ? simulateResult.value.send_plan
    : [];
  if (plan.length) return plan.map(item => item?.content).filter(Boolean);

  const responses = simulateResult.value?.response?.responses;
  if (Array.isArray(responses) && responses.length) {
    return responses.map(item => item?.content).filter(Boolean);
  }

  const single = simulateResult.value?.response?.response;
  return single ? [single] : [];
});

const debugTrace = computed(
  () =>
    simulateResult.value?.debug_trace ||
    simulateResult.value?.response_debug_trace ||
    {}
);
const featureCitationEnabled = computed(() => {
  const raw = props.assistant?.config?.feature_citation;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
});
const runtimeStatus = computed(() => aiCloserConfig.value?.status || 'inactive');
const articleScopeLabel = computed(() =>
  props.assistant?.config?.wlab_article_scope_mode === 'selected_portal'
    ? 'Pinned portal'
    : 'Inbox-aware'
);
const replyModeLabel = computed(() => {
  switch (String(props.assistant?.config?.wlab_ai_reply_mode || 'auto_reply').trim()) {
    case 'auto_suggest':
      return 'Auto suggest';
    case 'suggest':
      return 'Suggest only';
    case 'handoff':
      return 'Handoff';
    default:
      return 'Auto reply';
  }
});
const structuredGuidanceLines = computed(() =>
  (Array.isArray(promptSections.value.response_guidelines)
    ? promptSections.value.response_guidelines
    : []
  ).filter(line => /^\[AI Strategy:/.test(String(line || '').trim()))
);
const structuredGuardrailLines = computed(() =>
  (Array.isArray(promptSections.value.guardrails) ? promptSections.value.guardrails : []).filter(
    line => /^\[AI Guardrail:/.test(String(line || '').trim())
  )
);
const runtimeStrategyCards = computed(() => [
  {
    label: 'Knowledge scope',
    value: articleScopeLabel.value,
    note:
      articleScopeLabel.value === 'Pinned portal'
        ? 'Help Center knowledge is pinned to one portal.'
        : 'Article scope follows the selected inbox context at runtime.',
  },
  {
    label: 'Reply mode',
    value: replyModeLabel.value,
    note: 'Current assistant delivery strategy.',
  },
  {
    label: 'Structured answer rules',
    value: structuredGuidanceLines.value.length + structuredGuardrailLines.value.length,
    note: 'Structured strategy rows currently saved into real runtime prompt sections.',
  },
  {
    label: 'Citations',
    value: featureCitationEnabled.value ? 'On' : 'Off',
    note: featureCitationEnabled.value
      ? 'Replies may attach citations when supported sources are used.'
      : 'Source citations are currently disabled.',
  },
]);
const traceDeliveryMode = computed(() => {
  const refs = debugTrace.value.escalation_refs || {};
  return refs.delivery_mode || simulateResult.value?.response?.delivery_mode || '—';
});
const traceProcedureStatus = computed(() => {
  const execution = debugTrace.value.procedure_execution || {};
  return execution.status || execution.outcome || '—';
});
const openWorkspaceTab = tab => {
  const nextQuery = { ...(route.query || {}), tab };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;
  delete nextQuery.analyze_followup_intent;
  delete nextQuery.analyze_followup_target_version_label;
  delete nextQuery.analyze_followup_compare_version_label;
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  router.replace({ query: nextQuery });
};
const openLearningAction = action => {
  switch (action) {
    case 'content':
      openWorkspaceTab('train_content');
      break;
    case 'guidance':
      openWorkspaceTab('train_guidance');
      break;
    case 'procedures':
      openWorkspaceTab('train_procedures');
      break;
    case 'escalation':
      openWorkspaceTab('train_escalation');
      break;
    case 'deploy':
      openWorkspaceTab('deploy_channels');
      break;
    default:
      break;
  }
};
const openGuidanceWorkspaceWithSeed = ({
  editor = '',
  reason = '',
  queryText = '',
  source = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'train_guidance', guidance_seed: '1' };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;

  const normalizedEditor = String(editor || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedQuery = String(queryText || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedEditor) nextQuery.guidance_seed_editor = normalizedEditor;
  if (normalizedReason) nextQuery.guidance_seed_reason = normalizedReason;
  if (normalizedQuery) nextQuery.guidance_seed_query = normalizedQuery;
  if (normalizedSource) nextQuery.guidance_seed_source = normalizedSource;

  router.replace({ query: nextQuery });
};
const openDeployWorkspaceWithSeed = ({
  focus = '',
  reason = '',
  inboxId = '',
  source = '',
  confidenceLevel = '',
  confidenceRuns = '',
  confidenceTitle = '',
  compareBundle = null,
  batchBaseline = null,
  followupIntent = '',
  targetVersionLabel = '',
  compareVersionLabel = '',
  prefillNote = '',
  openVersions = false,
  sessionId = '',
  sessionLabel = '',
  sessionStep = '',
  sessionOrigin = '',
  sessionItemKey = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'deploy_channels', deploy_seed: '1' };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;
  delete nextQuery.analyze_followup_intent;
  delete nextQuery.analyze_followup_target_version_label;
  delete nextQuery.analyze_followup_compare_version_label;
  delete nextQuery.analyze_followup_session_id;
  delete nextQuery.analyze_followup_session_label;
  delete nextQuery.analyze_followup_session_step;
  delete nextQuery.analyze_followup_session_origin;
  delete nextQuery.analyze_followup_session_item_key;

  const normalizedFocus = String(focus || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedInboxId = String(inboxId || '').trim();
  const normalizedSource = String(source || '').trim();
  const normalizedConfidenceLevel = String(confidenceLevel || '').trim();
  const normalizedConfidenceRuns = String(confidenceRuns || '').trim();
  const normalizedConfidenceTitle = String(confidenceTitle || '').trim();
  const normalizedFollowupIntent = String(followupIntent || '').trim();
  const normalizedTargetVersionLabel = String(targetVersionLabel || '').trim();
  const normalizedCompareVersionLabel = String(compareVersionLabel || '').trim();
  const normalizedPrefillNote = String(prefillNote || '').trim();
  const normalizedOpenVersions = openVersions === true;
  const normalizedSessionId = String(sessionId || '').trim();
  const normalizedSessionLabel = String(sessionLabel || '').trim();
  const normalizedSessionStep = String(sessionStep || '').trim();
  const normalizedSessionOrigin = String(sessionOrigin || '').trim();
  const normalizedSessionItemKey = String(sessionItemKey || '').trim();
  const normalizedCompareBundle =
    compareBundle && typeof compareBundle === 'object' ? compareBundle : null;
  const normalizedBatchBaseline =
    batchBaseline && typeof batchBaseline === 'object' ? batchBaseline : null;

  if (normalizedFocus) nextQuery.deploy_seed_focus = normalizedFocus;
  if (normalizedReason) nextQuery.deploy_seed_reason = normalizedReason;
  if (normalizedInboxId) nextQuery.deploy_seed_inbox_id = normalizedInboxId;
  if (normalizedSource) nextQuery.deploy_seed_source = normalizedSource;
  if (normalizedConfidenceLevel) {
    nextQuery.deploy_seed_confidence_level = normalizedConfidenceLevel;
  }
  if (normalizedConfidenceRuns) {
    nextQuery.deploy_seed_confidence_runs = normalizedConfidenceRuns;
  }
  if (normalizedConfidenceTitle) {
    nextQuery.deploy_seed_confidence_title = normalizedConfidenceTitle;
  }
  if (normalizedFollowupIntent) {
    nextQuery.deploy_seed_followup_intent = normalizedFollowupIntent;
  }
  if (normalizedTargetVersionLabel) {
    nextQuery.deploy_seed_target_version_label = normalizedTargetVersionLabel;
  }
  if (normalizedCompareVersionLabel) {
    nextQuery.deploy_seed_compare_version_label = normalizedCompareVersionLabel;
  }
  if (normalizedPrefillNote) {
    nextQuery.deploy_seed_prefill_note = normalizedPrefillNote;
  }
  if (normalizedOpenVersions) {
    nextQuery.deploy_seed_open_versions = '1';
  }
  if (normalizedSessionId) {
    nextQuery.deploy_seed_session_id = normalizedSessionId;
  }
  if (normalizedSessionLabel) {
    nextQuery.deploy_seed_session_label = normalizedSessionLabel;
  }
  if (normalizedSessionStep) {
    nextQuery.deploy_seed_session_step = normalizedSessionStep;
  }
  if (normalizedSessionOrigin) {
    nextQuery.deploy_seed_session_origin = normalizedSessionOrigin;
  }
  if (normalizedSessionItemKey) {
    nextQuery.deploy_seed_session_item_key = normalizedSessionItemKey;
  }
  if (normalizedSessionId) {
    nextQuery.rollback_session_key = normalizedSessionId;
  }
  if (normalizedSessionItemKey) {
    nextQuery.rollback_session_item_key = normalizedSessionItemKey;
  }
  if (normalizedBatchBaseline) {
    const batchBaselineKey = String(
      normalizedBatchBaseline.baseline_key || normalizedBatchBaseline.baselineKey || ''
    ).trim();
    const batchSetKey = String(
      normalizedBatchBaseline.set_key || normalizedBatchBaseline.setKey || ''
    ).trim();
    const batchTitle = String(normalizedBatchBaseline.title || '').trim();
    const batchOverallLevel = String(
      normalizedBatchBaseline.overall_level ||
        normalizedBatchBaseline.overallLevel ||
        ''
    ).trim();
    const batchTotalCases = String(
      normalizedBatchBaseline.total_cases || normalizedBatchBaseline.totalCases || ''
    ).trim();
    const batchReadyCases = String(
      normalizedBatchBaseline.ready_cases || normalizedBatchBaseline.readyCases || ''
    ).trim();
    const batchWatchCases = String(
      normalizedBatchBaseline.watch_cases || normalizedBatchBaseline.watchCases || ''
    ).trim();
    const batchBlockedCases = String(
      normalizedBatchBaseline.blocked_cases || normalizedBatchBaseline.blockedCases || ''
    ).trim();
    const batchRanAt = String(
      normalizedBatchBaseline.ran_at || normalizedBatchBaseline.ranAt || ''
    ).trim();
    const batchTopBlocker = String(
      normalizedBatchBaseline.top_blocker || normalizedBatchBaseline.topBlocker || ''
    ).trim();
    const batchTopWatchSignal = String(
      normalizedBatchBaseline.top_watch_signal ||
        normalizedBatchBaseline.topWatchSignal ||
        ''
    ).trim();
    if (batchBaselineKey) {
      nextQuery.deploy_seed_batch_baseline_key = batchBaselineKey;
    }
    if (batchSetKey) nextQuery.deploy_seed_batch_set_key = batchSetKey;
    if (batchTitle) nextQuery.deploy_seed_batch_title = batchTitle;
    if (batchOverallLevel) {
      nextQuery.deploy_seed_batch_overall_level = batchOverallLevel;
    }
    if (batchTotalCases) nextQuery.deploy_seed_batch_total_cases = batchTotalCases;
    if (batchReadyCases) nextQuery.deploy_seed_batch_ready_cases = batchReadyCases;
    if (batchWatchCases) nextQuery.deploy_seed_batch_watch_cases = batchWatchCases;
    if (batchBlockedCases) {
      nextQuery.deploy_seed_batch_blocked_cases = batchBlockedCases;
    }
    if (batchRanAt) nextQuery.deploy_seed_batch_ran_at = batchRanAt;
    if (batchTopBlocker) nextQuery.deploy_seed_batch_top_blocker = batchTopBlocker;
    if (batchTopWatchSignal) {
      nextQuery.deploy_seed_batch_top_watch_signal = batchTopWatchSignal;
    }
  }
  if (normalizedCompareBundle) {
    const compareTitle = String(normalizedCompareBundle.title || '').trim();
    const compareFocusLabel = String(normalizedCompareBundle.focusLabel || '').trim();
    const compareStrategyGroupTitle = String(
      normalizedCompareBundle.strategyGroupTitle || ''
    ).trim();
    const compareRecommendationCode = String(
      normalizedCompareBundle.recommendationCode || ''
    ).trim();
    const compareOwnerWorkspace = String(
      normalizedCompareBundle.ownerWorkspace || ''
    ).trim();
    const compareOwnerActionTitle = String(
      normalizedCompareBundle.ownerActionTitle || ''
    ).trim();
    const compareGuidanceEditor = String(
      normalizedCompareBundle.guidanceEditor || ''
    ).trim();
    const compareEscalationFocus = String(
      normalizedCompareBundle.escalationFocus || ''
    ).trim();
    const compareHotspotSummary = String(
      normalizedCompareBundle.hotspotSummary || ''
    ).trim();
    const compareResolvedSummary = String(
      normalizedCompareBundle.resolvedSummary || ''
    ).trim();
    const compareEscalatedSummary = String(
      normalizedCompareBundle.escalatedSummary || ''
    ).trim();
    const compareValidationSummary = String(
      normalizedCompareBundle.validationSummary || ''
    ).trim();
    const compareStage = String(normalizedCompareBundle.stage || '').trim();
    const compareRollbackTrigger = String(
      normalizedCompareBundle.rollbackTrigger ||
        normalizedCompareBundle.validationRollbackTrigger ||
        ''
    ).trim();
    if (
      compareTitle ||
      compareHotspotSummary ||
      compareResolvedSummary ||
      compareEscalatedSummary ||
      compareValidationSummary
    ) {
      nextQuery.deploy_seed_bundle = '1';
    }
    if (compareTitle) nextQuery.deploy_seed_title = compareTitle;
    if (compareFocusLabel) nextQuery.deploy_seed_focus_label = compareFocusLabel;
    if (compareStrategyGroupTitle) {
      nextQuery.deploy_seed_strategy_group_title = compareStrategyGroupTitle;
    }
    if (compareRecommendationCode) {
      nextQuery.deploy_seed_recommendation_code = compareRecommendationCode;
    }
    if (compareOwnerWorkspace) {
      nextQuery.deploy_seed_owner_workspace = compareOwnerWorkspace;
    }
    if (compareOwnerActionTitle) {
      nextQuery.deploy_seed_owner_action_title = compareOwnerActionTitle;
    }
    if (compareGuidanceEditor) {
      nextQuery.deploy_seed_guidance_editor = compareGuidanceEditor;
    }
    if (compareEscalationFocus) {
      nextQuery.deploy_seed_escalation_focus = compareEscalationFocus;
    }
    if (compareHotspotSummary) {
      nextQuery.deploy_seed_hotspot_summary = compareHotspotSummary;
    }
    if (compareResolvedSummary) {
      nextQuery.deploy_seed_resolved_summary = compareResolvedSummary;
    }
    if (compareEscalatedSummary) {
      nextQuery.deploy_seed_escalated_summary = compareEscalatedSummary;
    }
    if (compareValidationSummary) {
      nextQuery.deploy_seed_validation_summary = compareValidationSummary;
    }
    if (compareStage) nextQuery.deploy_seed_stage = compareStage;
    if (compareRollbackTrigger) {
      nextQuery.deploy_seed_rollback_trigger = compareRollbackTrigger;
    }
  }

  router.replace({ query: nextQuery });
};
const openDeployRollbackFollowup = () => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome) return;
  const session = advanceTestRollbackSession('Deploy');
  openDeployWorkspaceWithSeed({
    focus: 'release',
    reason: 'rollback_followup_writeback',
    inboxId: selectedInboxId.value || seededTestContext.value.inboxId || '',
    source: 'Test',
    confidenceLevel: String(outcome.confidence_level || '').trim(),
    confidenceTitle: String(outcome.intent_label || outcome.recommended_action_label || '').trim(),
    compareBundle: buildRollbackFollowupCompareBundle(outcome),
    batchBaseline: releaseReadinessSummary.value?.latest_batch_baseline || null,
    followupIntent: String(
      outcome.operator_actions?.[0]?.intent_label || outcome.recommended_action_label || ''
    ).trim(),
    targetVersionLabel: String(outcome.target_version_label || '').trim(),
    compareVersionLabel: String(
      outcome.compare_version_label || outcome.previous_version_label || ''
    ).trim(),
    prefillNote: buildRollbackFollowupNote(outcome),
    sessionId: session.sessionId,
    sessionLabel: session.sessionLabel,
    sessionStep: String(session.sessionStep || 1),
    sessionOrigin: session.sessionOrigin || 'Test',
    sessionItemKey: currentTestRollbackSessionItemKey(),
  });
};
const openEscalationWorkspaceWithSeed = ({
  focus = '',
  reason = '',
  queryText = '',
  label = '',
  source = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'train_escalation', escalation_seed: '1' };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;

  const normalizedFocus = String(focus || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedQuery = String(queryText || '').trim();
  const normalizedLabel = String(label || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedFocus) nextQuery.escalation_seed_focus = normalizedFocus;
  if (normalizedReason) nextQuery.escalation_seed_reason = normalizedReason;
  if (normalizedQuery) nextQuery.escalation_seed_query = normalizedQuery;
  if (normalizedLabel) nextQuery.escalation_seed_label = normalizedLabel;
  if (normalizedSource) nextQuery.escalation_seed_source = normalizedSource;

  router.replace({ query: nextQuery });
};
const openContentWorkspaceWithSeed = ({
  queryText = '',
  reason = '',
  scopeWarning = '',
  focus = '',
  source = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'train_content', content_seed: '1' };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;
  delete nextQuery.guidance_seed;
  delete nextQuery.guidance_seed_source;
  delete nextQuery.guidance_seed_editor;
  delete nextQuery.guidance_seed_reason;
  delete nextQuery.guidance_seed_query;
  delete nextQuery.deploy_seed;
  delete nextQuery.deploy_seed_source;
  delete nextQuery.deploy_seed_focus;
  delete nextQuery.deploy_seed_reason;
  delete nextQuery.deploy_seed_inbox_id;
  delete nextQuery.escalation_seed;
  delete nextQuery.escalation_seed_source;
  delete nextQuery.escalation_seed_focus;
  delete nextQuery.escalation_seed_reason;
  delete nextQuery.escalation_seed_query;
  delete nextQuery.escalation_seed_label;

  const normalizedQuery = String(queryText || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedScopeWarning = String(scopeWarning || '').trim();
  const normalizedFocus = String(focus || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedQuery) nextQuery.content_seed_query = normalizedQuery;
  else delete nextQuery.content_seed_query;

  if (normalizedReason) nextQuery.content_seed_reason = normalizedReason;
  else delete nextQuery.content_seed_reason;

  if (normalizedScopeWarning) nextQuery.content_seed_scope_warning = normalizedScopeWarning;
  else delete nextQuery.content_seed_scope_warning;

  if (normalizedFocus) nextQuery.content_seed_focus = normalizedFocus;
  else delete nextQuery.content_seed_focus;

  if (normalizedSource) nextQuery.content_seed_source = normalizedSource;
  else delete nextQuery.content_seed_source;

  router.replace({ query: nextQuery });
};
const openTestAction = card => {
  if (card?.action === 'analyze_batch_row') {
    const row = batchSimulationRows.value.find(item => item.id === card.batchRowId);
    if (row) {
      openAnalyzeWithSeedContext({
        inboxId: String(row.inboxId || ''),
        contentGapReason: row.contentGapReason || '',
        articleScopeWarning: row.scopeWarning || '',
        deliveryMode: row.deliveryMode || '',
      });
    }
    return;
  }

  if (card?.action === 'deploy_batch') {
    const row = batchSimulationRows.value.find(item => item.id === card.batchRowId);
    openDeployWorkspaceWithSeed({
      focus: 'release',
      reason:
        batchSimulationSummary.value.blocked === 0 &&
        batchSimulationSummary.value.watch === 0
          ? 'batch_simulation_ready'
          : 'batch_simulation_review',
      inboxId: row?.inboxId || selectedInboxId.value || seededTestContext.value.inboxId || '',
      source: 'Test',
      confidenceLevel:
        batchSimulationSummary.value.blocked === 0 &&
        batchSimulationSummary.value.watch === 0
          ? 'ready'
          : 'observe',
      confidenceRuns: batchSimulationSummary.value.total,
      confidenceTitle:
        batchSimulationSummary.value.blocked === 0 &&
        batchSimulationSummary.value.watch === 0
          ? 'Batch simulation set cleared release checks'
          : 'Batch simulation set needs rollout review',
      batchBaseline: buildBatchBaselinePayload({
        rows: batchSimulationRows.value,
        summary: batchSimulationSummary.value,
      }),
      compareBundle: seededRepairBundle.value.enabled ? seededRepairBundle.value : null,
    });
    return;
  }

  if (card?.action === 'analyze') {
    openAnalyzeWithCurrentContext();
    return;
  }

  if (card?.action === 'content' && card?.contentSeed) {
    openContentWorkspaceWithSeed({
      queryText: card.contentSeed.queryText || '',
      reason: card.contentSeed.reason || '',
      scopeWarning: card.contentSeed.scopeWarning || '',
      focus: card.contentSeed.focus || '',
      source: 'Test',
    });
    return;
  }

  if (card?.action === 'guidance' && card?.guidanceSeed) {
    openGuidanceWorkspaceWithSeed({
      editor: card.guidanceSeed.editor || '',
      reason: card.guidanceSeed.reason || '',
      queryText: card.guidanceSeed.queryText || '',
      source: 'Test',
    });
    return;
  }

  if (card?.action === 'deploy' && card?.deploySeed) {
    openDeployWorkspaceWithSeed({
      focus: card.deploySeed.focus || '',
      reason: card.deploySeed.reason || '',
      inboxId: card.deploySeed.inboxId || '',
      source: 'Test',
      confidenceLevel: card.deploySeed.confidenceLevel || '',
      confidenceRuns: card.deploySeed.confidenceRuns || '',
      confidenceTitle: card.deploySeed.confidenceTitle || '',
      batchBaseline: card.deploySeed.batchBaseline || null,
      compareBundle: card.deploySeed.compareBundle || null,
    });
    return;
  }

  if (card?.action === 'escalation' && card?.escalationSeed) {
    openEscalationWorkspaceWithSeed({
      focus: card.escalationSeed.focus || '',
      reason: card.escalationSeed.reason || '',
      queryText: card.escalationSeed.queryText || '',
      label: card.escalationSeed.label || '',
      source: 'Test',
    });
    return;
  }

  openLearningAction(card?.action);
};
const prefillBatchFromCurrentQuery = () => {
  const current = String(batchInput.value || '').trim();
  const nextLine = String(currentTestQuery.value || '').trim();
  if (!nextLine) {
    alert('Run or seed a query first, then add it to the batch set.');
    return;
  }
  batchInput.value = current ? `${current}\n${nextLine}` : nextLine;
};

const saveCurrentBatchTestSet = async () => {
  if (!parsedBatchCases.value.length) {
    alert('Add at least one batch case before saving a test set.');
    return;
  }

  const title = String(batchSetTitleDraft.value || '').trim();
  if (!title) {
    alert('Name the batch test set before saving it.');
    return;
  }

  try {
    const payload = {
      title,
      set_key: currentSavedBatchTestSet.value?.set_key || selectedSavedBatchSetKey.value || '',
      default_inbox_id: selectedInboxId.value || '',
      cases: parsedBatchCases.value.map(item => ({
        inbox_id: item.inboxId ? Number(item.inboxId) : null,
        query: item.query,
      })),
    };
    let savedSet = null;
    if (currentSavedBatchTestSet.value?.id) {
      const response = await AiCloserAPI.updateSavedTestSet(
        props.assistant.id,
        currentSavedBatchTestSet.value.id,
        payload
      );
      savedSet = response?.data || null;
    } else {
      const response = await AiCloserAPI.createSavedTestSet(props.assistant.id, payload);
      savedSet = response?.data || null;
    }
    await loadSavedBatchTestSets();
    selectedSavedBatchSetKey.value =
      savedSet?.set_key || payload.set_key || selectedSavedBatchSetKey.value || '';
    await refreshReleaseReadinessSummary();
    alert('Saved this batch test set to the backend library.');
  } catch (error) {
    try {
      await saveCurrentBatchTestSetToConfigFallback(title);
      await refreshReleaseReadinessSummary();
      alert('Saved this batch test set to the config fallback.');
    } catch (fallbackError) {
      alert(
        fallbackError?.response?.data?.errors?.[0] ||
          error?.response?.data?.errors?.[0] ||
          fallbackError?.response?.data?.error ||
          error?.response?.data?.error ||
          fallbackError?.message ||
          error?.message ||
          'Failed to save batch test set'
      );
    }
  }
};

const loadSavedBatchTestSet = () => {
  if (!currentSavedBatchTestSet.value) {
    alert('Select a saved batch test set first.');
    return;
  }

  batchInput.value = formatSavedBatchTestSetInput(currentSavedBatchTestSet.value);
  batchSetTitleDraft.value = currentSavedBatchTestSet.value.title || '';
  selectedInboxId.value = String(currentSavedBatchTestSet.value.default_inbox_id || '');
  clearBatchResults();
};

const deleteSavedBatchTestSet = async () => {
  if (!currentSavedBatchTestSet.value) {
    alert('Select a saved batch test set first.');
    return;
  }

  try {
    if (currentSavedBatchTestSet.value.id) {
      await AiCloserAPI.deleteSavedTestSet(
        props.assistant.id,
        currentSavedBatchTestSet.value.id
      );
      await loadSavedBatchTestSets();
    } else {
      await deleteSavedBatchTestSetFromConfigFallback();
    }
    selectedSavedBatchSetKey.value = '';
    batchSetTitleDraft.value = '';
    clearBatchResults();
    await refreshReleaseReadinessSummary();
    alert('Saved batch test set removed.');
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to delete batch test set'
    );
  }
};

const exportSavedBatchTestSets = async () => {
  const fallbackPayload = buildSavedBatchTestSetLibraryExport({
    sets: savedBatchTestSets.value,
    source: 'config_fallback',
  });

  isExportingSavedSets.value = true;
  try {
    let payload = fallbackPayload;
    if (props.assistant?.id) {
      try {
        const response = await AiCloserAPI.exportSavedTestSets(props.assistant.id);
        payload = buildSavedBatchTestSetLibraryExport({
          sets: response?.data?.saved_test_sets || [],
          source: response?.data?.source || 'backend',
          exportedAt: response?.data?.exported_at || new Date().toISOString(),
        });
      } catch {
        payload = fallbackPayload;
      }
    }

    const filename = `ailab-saved-test-sets-${new Date().toISOString().slice(0, 10)}.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    alert('Saved test set library exported.');
  } catch (error) {
    alert(error?.message || 'Failed to export saved test set library');
  } finally {
    isExportingSavedSets.value = false;
  }
};

const openSavedBatchTestSetImport = () => {
  savedTestSetImportInputRef.value?.click?.();
};

const importSavedBatchTestSetsToConfigFallback = async sets => {
  let nextSets = normalizeSavedBatchTestSets(currentReleaseLoop.value?.saved_test_sets || []);
  sets.forEach(set => {
    nextSets = mergeSavedBatchTestSets({
      sets: nextSets,
      set,
      limit: 12,
    });
  });
  await saveReleaseLoopConfig({ saved_test_sets: nextSets });
  return nextSets;
};

const handleSavedBatchTestSetImport = async event => {
  const file = event?.target?.files?.[0];
  if (!file) return;

  isImportingSavedSets.value = true;
  try {
    const rawText = await file.text();
    const parsed = JSON.parse(rawText);
    const importedSets = normalizeSavedBatchTestSetLibraryImport(parsed);
    if (!importedSets.length) {
      alert('No valid saved test sets were found in that file.');
      return;
    }

    try {
      if (!props.assistant?.id) throw new Error('Assistant missing');
      await AiCloserAPI.importSavedTestSets(props.assistant.id, importedSets);
      await loadSavedBatchTestSets();
      alert(`Imported ${importedSets.length} saved test set${importedSets.length === 1 ? '' : 's'} into the backend library.`);
    } catch {
      const nextSets = await importSavedBatchTestSetsToConfigFallback(importedSets);
      savedBatchTestSetsData.value = [];
      selectedSavedBatchSetKey.value = nextSets[0]?.set_key || selectedSavedBatchSetKey.value;
      alert(`Imported ${importedSets.length} saved test set${importedSets.length === 1 ? '' : 's'} into the config fallback.`);
    }

    await refreshReleaseReadinessSummary();
  } catch (error) {
    alert(error?.message || 'Failed to import saved test set library');
  } finally {
    if (event?.target) event.target.value = '';
    isImportingSavedSets.value = false;
  }
};

const clearBatchResults = () => {
  batchSimulationRows.value = [];
};
const runBatchSimulation = async () => {
  if (!props.assistant?.id) return;
  if (!parsedBatchCases.value.length) {
    alert('Add at least one batch case first.');
    return;
  }

  isRunningBatch.value = true;
  batchSimulationRows.value = [];
  try {
    const payloadCases = parsedBatchCases.value.map(item => ({
      inbox_id: item.inboxId ? Number(item.inboxId) : null,
      query: item.query,
    }));
    const response = await AiCloserAPI.batchSimulateConfig(props.assistant.id, {
      cases: payloadCases,
      includePrompt: false,
      includeResponse: true,
      estimatedCompletionTokens: 250,
    });
    const responseRows = Array(response?.data?.cases) ? response.data.cases : [];
    const rows = responseRows.map((item, index) => {
      const sourceRow = parsedBatchCases.value[index] || {};
      const resultData = item?.simulation || null;
      const summary = summarizeBatchSimulationResult({
        result: resultData,
        runtimeStatus: runtimeStatus.value,
        connectedInboxCount: connectedInboxes.value.length,
      });
      return {
        ...sourceRow,
        ...summary,
        inboxId: String(item?.inbox_id || sourceRow.inboxId || ''),
        query: String(item?.query || sourceRow.query || '').trim(),
        label:
          sourceRow.label ||
          String(item?.query || sourceRow.query || '').trim().slice(0, 96),
        result: resultData,
      };
    });
    batchSimulationRows.value = rows;
    try {
      await persistBatchBaseline({ rows });
    } catch (saveError) {
      alert(
        saveError?.response?.data?.error ||
          saveError?.message ||
          'Batch baseline ran, but saving the durable baseline failed'
      );
    }
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Batch simulation failed');
  } finally {
    isRunningBatch.value = false;
  }
};
const openAnalyzeWithSeedContext = async ({
  inboxId = '',
  contentGapReason = '',
  articleScopeWarning = '',
  deliveryMode = '',
} = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'analyze_decision_log', analyze_seed: '1' };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });

  if (inboxId) nextQuery.analyze_inbox_id = inboxId;
  else delete nextQuery.analyze_inbox_id;

  if (contentGapReason) {
    nextQuery.analyze_content_gap_reason = contentGapReason;
  } else {
    delete nextQuery.analyze_content_gap_reason;
  }

  if (articleScopeWarning) {
    nextQuery.analyze_article_scope_warning = articleScopeWarning;
  } else {
    delete nextQuery.analyze_article_scope_warning;
  }

  if (deliveryMode) {
    nextQuery.analyze_delivery_mode = deliveryMode;
  } else {
    delete nextQuery.analyze_delivery_mode;
  }

  nextQuery.analyze_time_window = '7d';
  nextQuery.analyze_source = 'Test';

  await router.replace({ query: nextQuery });
};
const openAnalyzeWithCurrentContext = async () =>
  openAnalyzeWithSeedContext({
    inboxId: analyzeSeedContext.value.inboxId,
    contentGapReason: analyzeSeedContext.value.contentGapReason,
    articleScopeWarning: analyzeSeedContext.value.articleScopeWarning,
    deliveryMode: analyzeSeedContext.value.deliveryMode,
  });
const formatTraceLabel = value =>
  String(value || '')
    .trim()
    .replace(/_/g, ' ');
const summarizeGuidanceTrace = trace => {
  const guidanceRefs = trace?.guidance_refs || {};
  const parts = [];
  if (guidanceRefs.answer_length_strategy) {
    parts.push(`answer length "${formatTraceLabel(guidanceRefs.answer_length_strategy)}"`);
  }
  if (guidanceRefs.directness_strategy) {
    parts.push(`directness "${formatTraceLabel(guidanceRefs.directness_strategy)}"`);
  }
  if (guidanceRefs.sensitive_topics_strategy) {
    parts.push(
      `sensitive-topic handling "${formatTraceLabel(guidanceRefs.sensitive_topics_strategy)}"`
    );
  }
  if (guidanceRefs.outside_knowledge_strategy) {
    parts.push(
      `outside-knowledge rule "${formatTraceLabel(guidanceRefs.outside_knowledge_strategy)}"`
    );
  }
  return parts.join(', ');
};
const summarizeEscalationTrace = trace => {
  const escalationRefs = trace?.escalation_refs || {};
  const parts = [];
  if (escalationRefs.uncertain_action) {
    parts.push(`uncertain route "${formatTraceLabel(escalationRefs.uncertain_action)}"`);
  }
  if (escalationRefs.high_risk_action) {
    parts.push(`high-risk default "${formatTraceLabel(escalationRefs.high_risk_action)}"`);
  }
  return parts.join(', ');
};
const summarizeRetrievalTrace = trace => {
  const retrievalRefs = trace?.retrieval_refs || {};
  const parts = [];
  if (retrievalRefs.top_priority_source) {
    parts.push(`top source "${formatTraceLabel(retrievalRefs.top_priority_source)}"`);
  }
  if (retrievalRefs.search_balance) {
    parts.push(`search balance "${formatTraceLabel(retrievalRefs.search_balance)}"`);
  }
  if (retrievalRefs.min_confidence_score !== undefined) {
    parts.push(`min confidence "${formatTraceLabel(retrievalRefs.min_confidence_score)}"`);
  }
  if (retrievalRefs.citation_mode) {
    parts.push(`citation "${formatTraceLabel(retrievalRefs.citation_mode)}"`);
  }
  if (retrievalRefs.active_segment_binding_scope) {
    parts.push(
      `segment binding "${formatTraceLabel(retrievalRefs.active_segment_binding_scope)}"`
    );
  }
  if (retrievalRefs.no_evidence_behavior) {
    parts.push(
      `no-evidence fallback "${formatTraceLabel(retrievalRefs.no_evidence_behavior)}"`
    );
  }
  return parts.join(', ');
};
const countSimulationMessages = result => {
  const plan = Array.isArray(result?.send_plan) ? result.send_plan : [];
  if (plan.length) return plan.map(item => item?.content).filter(Boolean).length;

  const responses = result?.response?.responses;
  if (Array.isArray(responses) && responses.length) {
    return responses.map(item => item?.content).filter(Boolean).length;
  }

  return result?.response?.response ? 1 : 0;
};
const evaluateRepairBundleResult = (result, repairBundle) => {
  if (!repairBundle?.enabled) return null;
  if (!result) {
    return {
      status: 'pending',
      tone: 'info',
      title: 'Run validation simulation',
      body:
        'You already have the compare bundle. Run the updated query once so this page can judge whether the repair moved away from the escalated pattern.',
      nextStep:
        'After the run completes, compare this result against the hotspot, resolved example, and escalated example.',
      deliveryMode: '',
      sourceKind: '',
      contentGapReason: '',
      scopeWarning: '',
      guidanceSummary: '',
      escalationSummary: '',
      retrievalSummary: '',
      hasContentGap: false,
      hasScopeWarning: false,
      missingGuidanceCoverage: false,
      hasReply: false,
    };
  }

  const trace = result?.debug_trace || {};
  const deliveryMode = String(
    trace?.escalation_refs?.delivery_mode || result?.response?.delivery_mode || ''
  ).trim();
  const sourceKind = String(trace?.source_kind || '').trim();
  const contentGapReason = String(trace?.content_gap_reason || '').trim();
  const scopeWarning = String(trace?.article_scope_warning || '').trim();
  const guidanceSummary = summarizeGuidanceTrace(trace);
  const escalationSummary = summarizeEscalationTrace(trace);
  const retrievalSummary = summarizeRetrievalTrace(trace);
  const hasContentGap = Boolean(trace?.content_gap_detected || contentGapReason);
  const hasScopeWarning = Boolean(scopeWarning);
  const missingGuidanceCoverage =
    repairBundle.ownerWorkspace === 'guidance' &&
    Number(trace?.guidance_refs?.guardrails_count || 0) === 0 &&
    Number(trace?.guidance_refs?.response_guidelines_count || 0) === 0;
  const hasReply = countSimulationMessages(result) > 0;
  const escalatedPattern =
    deliveryMode === 'handoff' ||
    sourceKind === 'no_match' ||
    hasContentGap ||
    hasScopeWarning ||
    missingGuidanceCoverage;
  const cleanerPattern =
    hasReply &&
    deliveryMode !== 'handoff' &&
    sourceKind !== 'no_match' &&
    !hasContentGap &&
    !hasScopeWarning &&
    !missingGuidanceCoverage;

  if (cleanerPattern) {
    return {
      status: 'success',
      tone: 'success',
      title: 'This run looks better than the escalated pattern',
      body:
        'The latest simulation produced a cleaner result than the seeded escalated example and is moving closer to the resolved pattern.',
      nextStep:
        'Open Analyze to compare this fresh run with the original hotspot and confirm the improvement holds across similar traffic.',
      deliveryMode,
      sourceKind,
      contentGapReason,
      scopeWarning,
      guidanceSummary,
      escalationSummary,
      retrievalSummary,
      hasContentGap,
      hasScopeWarning,
      missingGuidanceCoverage,
      hasReply,
    };
  }

  if (escalatedPattern) {
    return {
      status: 'danger',
      tone: 'danger',
      title: 'This run still looks too close to the escalated pattern',
      body:
        'The latest simulation still carries one of the signals that made the earlier case risky, so the repair is not stable yet.',
      nextStep:
        'Open the owner workspace, tighten the rule again, and rerun this exact test before treating the repair as complete.',
      deliveryMode,
      sourceKind,
      contentGapReason,
      scopeWarning,
      guidanceSummary,
      escalationSummary,
      retrievalSummary,
      hasContentGap,
      hasScopeWarning,
      missingGuidanceCoverage,
      hasReply,
    };
  }

  return {
    status: 'warning',
    tone: 'warning',
    title: 'This run is improving, but it is not proven yet',
    body:
      'The latest simulation is no longer obviously bad, but it also has not shown enough clean evidence to call the repair finished.',
    nextStep:
      'Keep the same query, inspect the owner workspace one more time, and rerun until the trace is clearly cleaner than the escalated example.',
    deliveryMode,
    sourceKind,
    contentGapReason,
    scopeWarning,
    guidanceSummary,
    escalationSummary,
    retrievalSummary,
    hasContentGap,
    hasScopeWarning,
    missingGuidanceCoverage,
    hasReply,
  };
};
const formatValidationRunTime = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const traceGuidanceStrategySummary = computed(() =>
  summarizeGuidanceTrace(debugTrace.value)
);
const traceEscalationSummary = computed(() =>
  summarizeEscalationTrace(debugTrace.value)
);
const testNextActionCards = computed(() => {
  const actions = [];

  if (repairBundleConfidenceActionCard.value) {
    actions.push(repairBundleConfidenceActionCard.value);
  }

  if (debugTrace.value.article_scope_warning || debugTrace.value.source_kind === 'no_match') {
    actions.push({
      title: 'Check knowledge scope first',
      body:
        debugTrace.value.article_scope_warning
          ? `Runtime reported article scope warning: ${debugTrace.value.article_scope_warning}.`
          : 'The runtime did not find a strong knowledge match for this test.',
      action: 'content',
      actionLabel: 'Open Content',
      contentSeed: {
        focus: debugTrace.value.article_scope_warning ? 'retrieval' : 'recommendations',
        queryText: currentTestQuery.value,
        reason: debugTrace.value.article_scope_warning ? 'article_scope_warning' : 'no_match',
        scopeWarning: String(debugTrace.value.article_scope_warning || '').trim(),
      },
    });
  }

  if (
    Number(debugTrace.value.guidance_refs?.guardrails_count || 0) === 0 &&
    Number(debugTrace.value.guidance_refs?.response_guidelines_count || 0) === 0
  ) {
    actions.push({
      title: 'Add stronger answer instructions',
      body: traceGuidanceStrategySummary.value
        ? `This test still lacks strong guardrails or response-guideline coverage, even though the runtime showed ${traceGuidanceStrategySummary.value}.`
        : 'This test did not show meaningful guidance rules in the runtime trace yet.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
      guidanceSeed: {
        editor: 'response_guidelines',
        reason: 'low_guidance_coverage',
        queryText: currentTestQuery.value,
      },
    });
  }

  if (
    (debugTrace.value.content_gap_detected || debugTrace.value.content_gap_reason) &&
    !String(debugTrace.value.guidance_refs?.outside_knowledge_strategy || '').trim()
  ) {
    actions.push({
      title: 'Decide how AI handles missing knowledge',
      body:
        'The runtime flagged a content gap, but this trace did not expose an outside-knowledge safety rule yet.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
      guidanceSeed: {
        editor: 'guardrails',
        reason: 'missing_outside_knowledge_rule',
        queryText: currentTestQuery.value,
      },
    });
  }

  if (
    Object.keys(debugTrace.value.procedure_refs || {}).length > 0 ||
    Object.keys(debugTrace.value.procedure_execution || {}).length > 0
  ) {
    actions.push({
      title: 'Inspect the procedure path',
      body: `Procedure routing is part of this test result${traceProcedureStatus.value !== '—' ? ` (${traceProcedureStatus.value})` : ''}.`,
      action: 'procedures',
      actionLabel: 'Open Procedures',
    });
  }

  if (
    traceDeliveryMode.value !== '—' &&
    ['suggest', 'auto_suggest', 'handoff'].includes(String(traceDeliveryMode.value))
  ) {
    actions.push({
      title: 'Review routing and delivery mode',
      body: traceEscalationSummary.value
        ? `This simulation ended with delivery mode "${traceDeliveryMode.value}". Runtime defaults currently show ${traceEscalationSummary.value}.`
        : `This simulation ended with delivery mode "${traceDeliveryMode.value}".`,
      action:
        traceDeliveryMode.value === 'handoff' ? 'escalation' : 'deploy',
      actionLabel:
        traceDeliveryMode.value === 'handoff' ? 'Open Escalation' : 'Open Deploy',
      escalationSeed:
        traceDeliveryMode.value === 'handoff'
          ? {
              focus: 'defaults',
              reason: 'handoff_delivery_mode',
              queryText: currentTestQuery.value,
            }
          : null,
      deploySeed:
        traceDeliveryMode.value === 'handoff'
          ? null
          : {
              focus: 'channels',
              reason: String(traceDeliveryMode.value || 'delivery_mode').trim(),
              inboxId: analyzeSeedContext.value.inboxId,
            },
    });
  }

  if (!actions.length) {
    actions.push({
      title: 'This test looks structurally healthy',
      body: 'No obvious routing warning was detected from the current trace. Use Content, Guidance, or Deploy to fine-tune quality.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
    });
  }

  return actions.slice(0, 4);
});
const releaseCheckCards = computed(() => [
  {
    label: 'Runtime status',
    value: runtimeStatus.value === 'active' ? 'Ready' : 'Blocked',
    note:
      runtimeStatus.value === 'active'
        ? 'This assistant is active in runtime.'
        : 'The runtime is inactive, so this result is still pre-release.',
  },
  {
    label: 'Connected inboxes',
    value: connectedInboxes.value.length,
    note:
      connectedInboxes.value.length > 0
        ? 'At least one live channel is available for rollout.'
        : 'No inbox is connected yet.',
  },
  {
    label: 'Version baseline',
    value: hasUnpublishedVersion.value
      ? `Draft ${draftVersionId.value}`
      : publishedVersionId.value
        ? `Live ${publishedVersionId.value}`
        : 'Unknown',
    note: hasUnpublishedVersion.value
      ? `A newer draft version ID ${draftVersionId.value} is ahead of live version ID ${publishedVersionId.value || 'none'}. Publish review still belongs in Deploy.`
      : publishedVersionId.value
        ? `Live version ID ${publishedVersionId.value} is the current release baseline for this simulation.`
        : 'No published version id is available yet.',
  },
  {
    label: 'Rollout split',
    value: releaseReadinessSummary.value?.split_rollout_count || 0,
    note:
      Number(releaseReadinessSummary.value?.split_rollout_count || 0) > 0
        ? `${releaseReadinessSummary.value.split_rollout_count} split rollout${releaseReadinessSummary.value.split_rollout_count === 1 ? '' : 's'} are active, with ${releaseReadinessSummary.value?.pinned_rollout_count || 0} pinned rollout${Number(releaseReadinessSummary.value?.pinned_rollout_count || 0) === 1 ? '' : 's'} also overriding specific inboxes.`
        : `No percentage-based split rollout is active right now${Number(releaseReadinessSummary.value?.pinned_rollout_count || 0) > 0 ? `, but ${releaseReadinessSummary.value.pinned_rollout_count} pinned rollout${releaseReadinessSummary.value.pinned_rollout_count === 1 ? '' : 's'} still override inbox traffic.` : '.'}`,
  },
  {
    label: 'Simulation rollout arm',
    value: simulateResult.value?.rollout_arm
      ? String(simulateResult.value.rollout_arm)
      : 'published',
    note: simulateResult.value?.rollout_mode === 'split'
      ? `This simulation resolved to the ${simulateResult.value.rollout_arm || 'control'} arm using bucket ${simulateResult.value?.rollout_assignment_bucket || 'n/a'} within the ${simulateResult.value?.rollout_traffic_percentage || 0}% rollout slice.`
      : simulateResult.value?.rollout_mode === 'pinned'
        ? `This inbox is pinned to rollout version ${simulateResult.value?.rollout_version_id || 'unknown'} instead of inheriting the global baseline.`
        : 'This simulation currently inherits the published baseline rather than a rollout override.',
  },
  {
    label: 'Release evidence',
    value: releaseReadinessSummary.value?.latest_batch_baseline?.overall_level || 'idle',
    note: releaseReadinessSummary.value?.latest_batch_baseline?.title
      ? `${releaseReadinessSummary.value.latest_batch_baseline.title}${releaseReadinessSummary.value.latest_batch_baseline.ran_at ? ` • ${formatValidationRunTime(releaseReadinessSummary.value.latest_batch_baseline.ran_at)}` : ''}`
      : 'No durable batch baseline has been captured yet.',
  },
  {
    label: 'Latest rollback outcome',
    value: releaseReadinessSummary.value?.latest_rollback_outcome?.status || 'none',
    note: releaseReadinessSummary.value?.latest_rollback_outcome?.summary
      ? `${releaseReadinessSummary.value.latest_rollback_outcome.summary}${releaseReadinessSummary.value.latest_rollback_outcome.operator_note ? ` • ${releaseReadinessSummary.value.latest_rollback_outcome.operator_note}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.confidence_label ? ` • ${releaseReadinessSummary.value.latest_rollback_outcome.confidence_label}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.recommended_action_label ? ` • ${releaseReadinessSummary.value.latest_rollback_outcome.recommended_action_label}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.operator_actions?.[0]?.intent_label ? ` • next in Deploy: ${releaseReadinessSummary.value.latest_rollback_outcome.operator_actions[0].intent_label}` : releaseReadinessSummary.value.latest_rollback_outcome.operator_actions?.[0]?.label ? ` • next in Deploy: ${releaseReadinessSummary.value.latest_rollback_outcome.operator_actions[0].label}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.evidence_gap_count ? ` • ${releaseReadinessSummary.value.latest_rollback_outcome.evidence_gap_count} evidence gap${releaseReadinessSummary.value.latest_rollback_outcome.evidence_gap_count === 1 ? '' : 's'}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.bundle_label ? ` • ${releaseReadinessSummary.value.latest_rollback_outcome.bundle_label}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.target_version_label ? ` • compare ${releaseReadinessSummary.value.latest_rollback_outcome.target_version_label}${releaseReadinessSummary.value.latest_rollback_outcome.previous_version_label ? ` vs ${releaseReadinessSummary.value.latest_rollback_outcome.previous_version_label}` : ''}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.helped_signals?.length ? ` • helped by ${releaseReadinessSummary.value.latest_rollback_outcome.helped_signals.join(', ')}` : ''}${releaseReadinessSummary.value.latest_rollback_outcome.risky_signals?.length ? ` • risky on ${releaseReadinessSummary.value.latest_rollback_outcome.risky_signals.join(', ')}` : ''}${releaseReadinessSummary.value?.bundle_rollback_outcome_counts ? ` • helped ${releaseReadinessSummary.value.bundle_rollback_outcome_counts.helped || 0} / risky ${releaseReadinessSummary.value.bundle_rollback_outcome_counts.risky || 0} / waiting ${releaseReadinessSummary.value.bundle_rollback_outcome_counts.waiting || 0}` : ''}`
      : 'No rollback outcome comparison is available yet.',
  },
  {
    label: 'Saved set library',
    value: releaseReadinessSummary.value?.saved_test_set_count || savedBatchTestSets.value.length,
    note: releaseReadinessSummary.value?.latest_saved_test_set?.title
      ? `Latest backend set ${releaseReadinessSummary.value.latest_saved_test_set.title}.`
      : 'No backend saved test set is available yet.',
  },
  {
    label: 'Trace delivery mode',
    value: traceDeliveryMode.value,
    note:
      traceDeliveryMode.value === '—'
        ? 'Run a simulation to inspect delivery routing.'
        : 'Use this to confirm whether the answer auto-replies, suggests, or hands off.',
  },
  {
    label: 'Article scope warning',
    value: debugTrace.value.article_scope_warning ? 'Attention' : 'Clear',
    note: debugTrace.value.article_scope_warning
      ? String(debugTrace.value.article_scope_warning)
      : 'No article-scope warning was returned by the latest trace.',
  },
]);
const batchSummaryCards = computed(() => [
  {
    label: 'Cases',
    value: batchSimulationSummary.value.total,
    note:
      batchSimulationSummary.value.total > 0
        ? 'Parsed cases in the current batch set.'
        : 'Paste one case per line to build a batch set.',
  },
  {
    label: 'Ready',
    value: batchSimulationSummary.value.ready,
    note: 'Cases with clean output and no immediate release blockers.',
  },
  {
    label: 'Watch',
    value: batchSimulationSummary.value.watch,
    note: 'Cases that still need a manual review of delivery mode or handling style.',
  },
  {
    label: 'Blocked',
    value: batchSimulationSummary.value.blocked,
    note: 'Cases with content gaps, scope warnings, no output, or missing rollout readiness.',
  },
]);
const topBlockedBatchRow = computed(
  () => batchSimulationRows.value.find(row => row.level === 'blocked') || null
);
const topReadyBatchRow = computed(
  () => batchSimulationRows.value.find(row => row.level === 'ready') || null
);
const batchNextActionCards = computed(() => {
  if (!batchSimulationRows.value.length) return [];

  const actions = [];
  if (topBlockedBatchRow.value) {
    actions.push({
      title: 'Investigate the top blocked batch case',
      body:
        topBlockedBatchRow.value.blockers?.[0] ||
        'At least one case still has a release blocker.',
      action: 'analyze_batch_row',
      actionLabel: 'Open Analyze',
      batchRowId: topBlockedBatchRow.value.id,
    });
  }
  if (
    batchSimulationSummary.value.total > 0 &&
    batchSimulationSummary.value.blocked === 0 &&
    topReadyBatchRow.value
  ) {
    actions.push({
      title:
        batchSimulationSummary.value.watch > 0
          ? 'Review batch rollout in Deploy'
          : 'Batch looks strong enough for deploy review',
      body:
        batchSimulationSummary.value.watch > 0
          ? 'No hard blockers remain, but some cases still need a manual review of delivery behavior.'
          : 'The whole batch cleared the first-pass release checks. Use Deploy to review live rollout decisions.',
      action: 'deploy_batch',
      actionLabel: 'Open Deploy',
      batchRowId: topReadyBatchRow.value.id,
    });
  }
  return actions.slice(0, 2);
});

const seededContextCard = computed(() => {
  if (!seededTestContext.value.query) return null;
  return {
    title: 'Seeded test context',
    body:
      seededTestContext.value.reason
        ? `This test was prefilled from ${seededTestContext.value.source || 'another workspace'} because of ${seededTestContext.value.reason}.`
        : `This test was prefilled from ${seededTestContext.value.source || 'another workspace'}.`,
  };
});
const seededRollbackFollowupCard = computed(() => {
  if (
    !seededRollbackFollowup.value.intent &&
    !seededRollbackFollowup.value.targetVersionLabel &&
    !seededRollbackFollowup.value.compareVersionLabel
  ) {
    return null;
  }
  return {
    title: 'Rollback follow-up context',
    body: `${seededRollbackFollowup.value.intent || 'Rollback operator follow-up'}${seededRollbackFollowup.value.targetVersionLabel ? ` • ${seededRollbackFollowup.value.targetVersionLabel}` : ''}${seededRollbackFollowup.value.compareVersionLabel ? ` vs ${seededRollbackFollowup.value.compareVersionLabel}` : ''}.`,
  };
});
const rollbackWritebackCard = computed(() => {
  if (seededRollbackFollowupCard.value) return seededRollbackFollowupCard.value;
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return null;
  return {
    title: 'Rollback follow-up context',
    body: buildRollbackFollowupNote(outcome),
  };
});
const rollbackEvidenceHistoryEntries = computed(() => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return [];
  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  return mergeOperatorNoteTimelineEntries({
    records: [snapshot, baseline].filter(Boolean),
    limit: 3,
  });
});
const rollbackEvidenceHistoryTotalCount = computed(() => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return 0;
  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  return mergeOperatorNoteTimelineEntries({
    records: [snapshot, baseline].filter(Boolean),
    limit: 25,
  }).length;
});
const rollbackEvidenceHistoryEntryKey = (entry, index = 0) =>
  `${entry?.created_at || 'no-time'}::${entry?.source || 'unknown'}::${entry?.body || index}`;
const rollbackEvidenceHistoryEntryMeta = entry => {
  const parts = [];
  if (entry?.source) parts.push(`via ${entry.source}`);
  if (entry?.created_at) parts.push(formatDateTime(entry.created_at));
  const context = buildOperatorNoteTimelineContextLabel(entry);
  if (context) parts.push(context);
  return parts.join(' • ');
};
const rollbackEvidenceHistoryEntryBadges = entry =>
  buildOperatorNoteTimelineBadges(entry);
const rollbackEvidenceDrilldownBadges = computed(() => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return [];
  const session = currentTestRollbackSession();
  return buildRollbackBreadcrumbBadges({
    source: 'Test',
    focus: 'release',
    intentLabel:
      outcome.operator_actions?.[0]?.intent_label || outcome.recommended_action_label,
    targetVersionLabel: outcome.target_version_label,
    compareVersionLabel: outcome.compare_version_label || outcome.previous_version_label,
    confidenceLabel: outcome.confidence_label,
    nextLabel: 'open deploy evidence',
    sessionLabel: session.sessionLabel,
    sessionStep: session.sessionStep,
    sessionOrigin: session.sessionOrigin,
  });
});
const buildRollbackEvidenceDrilldownPayload = ({ openVersions = false } = {}) => {
  const outcome = releaseReadinessSummary.value?.latest_rollback_outcome || null;
  if (!outcome?.summary) return null;
  const { snapshot, baseline } = findRollbackEvidenceTargets(outcome);
  const primaryRecord = snapshot || baseline || null;
  const primaryEntry = rollbackEvidenceHistoryEntries.value[0] || null;
  const session = advanceTestRollbackSession('Deploy');
  const versionContext = buildOperatorNoteTimelineVersionDialogContext({
    record: primaryRecord || buildRollbackFollowupCompareBundle(outcome),
    entry: primaryEntry,
    fallbackIntent:
      outcome.operator_actions?.[0]?.intent_label ||
      outcome.recommended_action_label ||
      'Rollback evidence timeline',
    sessionContext: session,
  });
  return {
    focus: 'release',
    reason: openVersions ? 'rollback_evidence_versions' : 'rollback_evidence_timeline',
    inboxId: selectedInboxId.value || seededTestContext.value.inboxId || '',
    source: 'Test',
    confidenceLevel: String(outcome.confidence_level || '').trim(),
    confidenceTitle: String(outcome.confidence_label || outcome.confidence_note || '').trim(),
    compareBundle: primaryRecord
      ? buildRollbackEvidenceCompareBundle({
          record: primaryRecord,
          ownerWorkspace: 'test',
          ownerActionTitle: versionContext.intent_label,
        })
      : buildRollbackFollowupCompareBundle(outcome),
    batchBaseline: baseline || null,
    followupIntent: versionContext.intent_label,
    targetVersionLabel:
      versionContext.target_version_label || String(outcome.target_version_label || '').trim(),
    compareVersionLabel:
      versionContext.compare_version_label ||
      String(outcome.compare_version_label || outcome.previous_version_label || '').trim(),
    openVersions,
    sessionId: session.sessionId,
    sessionLabel: session.sessionLabel,
    sessionStep: String(session.sessionStep || 1),
    sessionOrigin: session.sessionOrigin || 'Test',
    sessionItemKey: currentTestRollbackSessionItemKey(),
  };
};
const openDeployRollbackEvidence = ({ openVersions = false } = {}) => {
  const payload = buildRollbackEvidenceDrilldownPayload({ openVersions });
  if (!payload) return;
  openDeployWorkspaceWithSeed(payload);
};
const currentRepairValidationSummary = computed(() => {
  if (!seededRepairBundle.value.enabled) return '';
  if (!simulateResult.value) {
    return 'Run a fresh simulation after editing the owner workspace, then compare this result against the hotspot, resolved example, and escalated example below.';
  }

  const parts = [];
  if (traceDeliveryMode.value && traceDeliveryMode.value !== '—') {
    parts.push(`delivery "${traceDeliveryMode.value}"`);
  }
  if (debugTrace.value.content_gap_reason) {
    parts.push(`content gap "${debugTrace.value.content_gap_reason}"`);
  }
  if (traceGuidanceStrategySummary.value) {
    parts.push(traceGuidanceStrategySummary.value);
  }
  if (traceEscalationSummary.value) {
    parts.push(traceEscalationSummary.value);
  }
  return parts.length
    ? `Latest run shows ${parts.join(' • ')}.`
    : 'Latest run completed. Compare the current reply and trace against the seeded examples below.';
});
const repairBundleVerdict = computed(() =>
  evaluateRepairBundleResult(simulateResult.value, seededRepairBundle.value)
);
const repairBundleVerdictSignals = computed(() => {
  if (!seededRepairBundle.value.enabled || !repairBundleVerdict.value) return [];

  const signals = [];
  if (repairBundleVerdict.value.deliveryMode) {
    signals.push(`Current delivery mode: ${repairBundleVerdict.value.deliveryMode}`);
  }
  if (repairBundleVerdict.value.sourceKind) {
    signals.push(`Current source kind: ${repairBundleVerdict.value.sourceKind}`);
  }
  if (repairBundleVerdict.value.contentGapReason) {
    signals.push(`Current content gap: ${repairBundleVerdict.value.contentGapReason}`);
  } else {
    signals.push('Current content gap: none detected');
  }
  if (repairBundleVerdict.value.scopeWarning) {
    signals.push(`Current scope warning: ${repairBundleVerdict.value.scopeWarning}`);
  } else {
    signals.push('Current scope warning: clear');
  }
  if (repairBundleVerdict.value.guidanceSummary) {
    signals.push(`Guidance runtime: ${repairBundleVerdict.value.guidanceSummary}`);
  }
  if (repairBundleVerdict.value.escalationSummary) {
    signals.push(`Escalation runtime: ${repairBundleVerdict.value.escalationSummary}`);
  }
  return signals;
});
const repairBundleValidationChecklist = computed(() => {
  if (!seededRepairBundle.value.enabled) return [];

  const hasRun = Boolean(simulateResult.value);

  return [
    {
      label: 'Run the same seeded query after your latest edit',
      passed: hasRun,
    },
    {
      label: 'Current run no longer falls into immediate handoff or no-match',
      passed:
        hasRun &&
        repairBundleVerdict.value?.deliveryMode !== 'handoff' &&
        repairBundleVerdict.value?.sourceKind !== 'no_match',
    },
    {
      label: 'Current run no longer shows a knowledge gap or scope warning',
      passed:
        hasRun &&
        !repairBundleVerdict.value?.hasContentGap &&
        !repairBundleVerdict.value?.hasScopeWarning,
    },
  ];
});
const repairBundleCompareCards = computed(() => {
  if (!seededRepairBundle.value.enabled) return [];

  return [
    {
      key: 'hotspot',
      label: 'Hotspot',
      body: seededRepairBundle.value.hotspotSummary,
      active: seededRepairBundle.value.stage === 'hotspot',
    },
    {
      key: 'resolved',
      label: 'Resolved example',
      body: seededRepairBundle.value.resolvedSummary,
      active: seededRepairBundle.value.stage === 'resolved',
    },
    {
      key: 'escalated',
      label: 'Escalated example',
      body: seededRepairBundle.value.escalatedSummary,
      active: seededRepairBundle.value.stage === 'escalated',
    },
    {
      key: 'validation',
      label: 'Validation',
      body: seededRepairBundle.value.validationSummary,
      extra: currentRepairValidationSummary.value,
      active:
        !seededRepairBundle.value.stage ||
        seededRepairBundle.value.stage === 'validation',
    },
  ].filter(card => card.body || card.extra);
});
const repairBundleOwnerAction = computed(() => {
  if (!seededRepairBundle.value.enabled) return null;

  const ownerReason =
    seededRepairBundle.value.focusLabel ||
    seededRepairBundle.value.title ||
    seededTestContext.value.reason ||
    'strategy_repair_bundle';
  if (seededRepairBundle.value.ownerWorkspace === 'escalation') {
    return {
      action: 'escalation',
      actionLabel:
        seededRepairBundle.value.ownerActionTitle || 'Open Escalation hotspot',
      escalationSeed: {
        focus: seededRepairBundle.value.escalationFocus || 'defaults',
        reason: ownerReason,
        queryText: currentTestQuery.value,
        label: seededRepairBundle.value.strategyGroupTitle || '',
      },
    };
  }
  if (seededRepairBundle.value.ownerWorkspace === 'guidance') {
    return {
      action: 'guidance',
      actionLabel:
        seededRepairBundle.value.ownerActionTitle || 'Open Guidance hotspot',
      guidanceSeed: {
        editor:
          seededRepairBundle.value.guidanceEditor || 'response_guidelines',
        reason: ownerReason,
        queryText: currentTestQuery.value,
      },
    };
  }

  return null;
});
const repairBundleConfidence = computed(() => {
  if (!seededRepairBundle.value.enabled) return null;
  return summarizeRepairBundleConfidence(repairValidationRuns.value);
});
const repairBundleReleaseReview = computed(() => {
  if (!seededRepairBundle.value.enabled) return null;
  return findReleaseReviewSnapshot({
    snapshots: currentReleaseSnapshots.value,
    bundle: seededRepairBundle.value,
  });
});
const repairValidationRunRows = computed(() =>
  repairValidationRuns.value.map((run, index) => ({
    ...run,
    label: `Run ${repairValidationRuns.value.length - index}`,
    timeLabel: formatValidationRunTime(run.ranAt),
  }))
);
const repairBundleConfidenceActionCard = computed(() => {
  if (!seededRepairBundle.value.enabled || !repairBundleConfidence.value) return null;

  if (repairBundleReleaseReview.value?.status === 'released') {
    return {
      title: 'Release review already marked released',
      body:
        repairBundleReleaseReview.value?.note ||
        'Open Deploy if you need to revise the release snapshot or change the rollback decision.',
      action: 'deploy',
      actionLabel: 'Open Deploy',
      deploySeed: {
        focus: 'release',
        reason: 'release_snapshot_revisit',
        inboxId: selectedInboxId.value || seededTestContext.value.inboxId || '',
        confidenceLevel:
          repairBundleReleaseReview.value?.confidence_level ||
          repairBundleConfidence.value.level,
        confidenceRuns:
          repairBundleReleaseReview.value?.confidence_runs ||
          repairValidationRuns.value.length,
        confidenceTitle:
          repairBundleReleaseReview.value?.confidence_title ||
          repairBundleConfidence.value.title,
        compareBundle: seededRepairBundle.value,
      },
    };
  }

  if (repairBundleConfidence.value.level === 'ready') {
    return {
      title: 'Confidence is high enough for release review',
      body: hasUnpublishedVersion.value
        ? `${repairBundleConfidence.value.nextStep} Draft version ID ${draftVersionId.value} is ahead of live version ID ${publishedVersionId.value || 'none'}, so this repair still needs publish review in Deploy.`
        : repairBundleConfidence.value.nextStep,
      action: 'deploy',
      actionLabel: 'Open Deploy',
      deploySeed: {
        focus: 'release',
        reason: 'repair_confidence_ready',
        inboxId: selectedInboxId.value || seededTestContext.value.inboxId || '',
        confidenceLevel: repairBundleConfidence.value.level,
        confidenceRuns: repairValidationRuns.value.length,
        confidenceTitle: repairBundleConfidence.value.title,
        compareBundle: seededRepairBundle.value,
      },
    };
  }

  if (repairBundleOwnerAction.value) {
    return {
      title: repairBundleConfidence.value.title,
      body: repairBundleConfidence.value.nextStep,
      ...repairBundleOwnerAction.value,
    };
  }

  return {
    title: repairBundleConfidence.value.title,
    body: repairBundleConfidence.value.nextStep,
    action: 'analyze',
    actionLabel: 'Open Analyze',
  };
});
const traceWarningCards = computed(() => {
  const warnings = [];

  if (debugTrace.value.article_scope_warning) {
    warnings.push({
      title: 'Knowledge scope warning',
      body: String(debugTrace.value.article_scope_warning),
      action: 'content',
      actionLabel: 'Open Content',
      contentSeed: {
        focus: 'retrieval',
        queryText: currentTestQuery.value,
        reason: 'article_scope_warning',
        scopeWarning: String(debugTrace.value.article_scope_warning || '').trim(),
      },
    });
  }

  if (debugTrace.value.content_gap_detected || debugTrace.value.content_gap_reason) {
    warnings.push({
      title: 'Content gap detected',
      body:
        String(debugTrace.value.content_gap_reason || '').trim() ||
        'The runtime marked this test as missing enough usable knowledge.',
      action: 'content',
      actionLabel: 'Open Content',
      contentSeed: {
        focus: 'recommendations',
        queryText: String(debugTrace.value.content_gap_query_preview || currentTestQuery.value || '').trim(),
        reason: String(debugTrace.value.content_gap_reason || 'content_gap').trim(),
      },
    });
  }

  if (
    Number(debugTrace.value.guidance_refs?.guardrails_count || 0) === 0 &&
    Number(debugTrace.value.guidance_refs?.response_guidelines_count || 0) === 0
  ) {
    warnings.push({
      title: 'Guidance did not meaningfully shape this reply',
      body: traceGuidanceStrategySummary.value
        ? `This trace did not show strong guardrail or response-guideline coverage. The runtime only exposed ${traceGuidanceStrategySummary.value}.`
        : 'This trace did not show strong guardrail or response-guideline coverage.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
      guidanceSeed: {
        editor: 'response_guidelines',
        reason: 'low_guidance_coverage',
        queryText: currentTestQuery.value,
      },
    });
  }

  if (
    (debugTrace.value.content_gap_detected || debugTrace.value.content_gap_reason) &&
    !String(debugTrace.value.guidance_refs?.outside_knowledge_strategy || '').trim()
  ) {
    warnings.push({
      title: 'Missing outside-knowledge safety rule',
      body:
        'The AI hit a knowledge gap, but the trace did not expose a configured rule for what to do when knowledge is missing.',
      action: 'guidance',
      actionLabel: 'Open Guidance',
      guidanceSeed: {
        editor: 'guardrails',
        reason: 'missing_outside_knowledge_rule',
        queryText: currentTestQuery.value,
      },
    });
  }

  if (traceDeliveryMode.value === 'handoff') {
    warnings.push({
      title: 'Simulation ended in handoff',
      body: traceEscalationSummary.value
        ? `Check whether escalation rules are too aggressive or whether this path should truly hand off. Runtime defaults show ${traceEscalationSummary.value}.`
        : 'Check whether escalation rules are too aggressive or whether this path should truly hand off.',
      action: 'escalation',
      actionLabel: 'Open Escalation',
      escalationSeed: {
        focus: 'defaults',
        reason: 'handoff_delivery_mode',
        queryText: currentTestQuery.value,
      },
    });
  }

  if (!warnings.length && simulateResult.value) {
    warnings.push({
      title: 'No critical warning from the latest trace',
      body: 'The current simulation does not show a dominant scope, guidance, or routing problem.',
      action: 'deploy',
      actionLabel: 'Open Deploy',
      deploySeed: {
        focus: 'release',
        reason: 'healthy_trace_check',
        inboxId: analyzeSeedContext.value.inboxId,
      },
    });
  }

  return warnings.slice(0, 4);
});

const formatTraceValue = value => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const buildKeyValueRows = value => {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value)
    .map(([key, item]) => [key, formatTraceValue(item)])
    .filter(([, item]) => item !== '—');
};

const applySeededContext = () => {
  if (seededTestContext.value.query) {
    query.value = seededTestContext.value.query;
  }
  if (seededTestContext.value.inboxId) {
    selectedInboxId.value = seededTestContext.value.inboxId;
  }
};

const clearSeededContext = async () => {
  const nextQuery = { ...(route.query || {}) };
  TEST_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  await router.replace({ query: nextQuery });
};

const traceRows = computed(() => [
  ['Source kind', formatTraceValue(debugTrace.value.source_kind)],
  ['Reason', formatTraceValue(debugTrace.value.reason)],
  ['Reply language', formatTraceValue(debugTrace.value.reply_language)],
  ['Intent', formatTraceValue(debugTrace.value.intent)],
  ['Distance', formatTraceValue(debugTrace.value.distance)],
  ['Article ID', formatTraceValue(debugTrace.value.article_id)],
  ['Template ID', formatTraceValue(debugTrace.value.template_id)],
  ['Article scope', formatTraceValue(debugTrace.value.article_scope)],
  ['Article scope warning', formatTraceValue(debugTrace.value.article_scope_warning)],
  [
    'Retrieval fallback',
    formatTraceValue(debugTrace.value.retrieval_no_evidence_behavior),
  ],
  [
    'Retrieval min confidence',
    formatTraceValue(debugTrace.value.retrieval_min_confidence_score),
  ],
  [
    'Retrieval search balance',
    formatTraceValue(debugTrace.value.retrieval_search_balance),
  ],
  [
    'Retrieval citation mode',
    formatTraceValue(debugTrace.value.retrieval_citation_mode),
  ],
  [
    'Retrieval active segment binding',
    formatTraceValue(debugTrace.value.retrieval_active_segment_binding_scope),
  ],
  [
    'Retrieval active inbox binding',
    formatTraceValue(debugTrace.value.retrieval_active_inbox_binding_scope),
  ],
  ['Retrieval budget', formatTraceValue(debugTrace.value.retrieval_context_budget)],
  ['Retrieval max sources', formatTraceValue(debugTrace.value.retrieval_max_sources)],
].filter(([, value]) => value !== '—'));

const contextCountRows = computed(() =>
  buildKeyValueRows(debugTrace.value.context_counts)
);

const contextRefRows = computed(() =>
  buildKeyValueRows(debugTrace.value.context_refs)
);

const retrievalRefRows = computed(() =>
  buildKeyValueRows(debugTrace.value.retrieval_refs)
);

const guidanceRefRows = computed(() =>
  buildKeyValueRows({
    used_sections: debugTrace.value.guidance_refs?.used_sections,
    used_sections_count: debugTrace.value.guidance_refs?.used_sections_count,
    guidance_card_keys: debugTrace.value.guidance_refs?.guidance_card_keys,
    guidance_card_count: debugTrace.value.guidance_refs?.guidance_card_count,
    guidance_scenarios: debugTrace.value.guidance_refs?.guidance_scenarios,
    guidance_scenario_keys: debugTrace.value.guidance_refs?.guidance_scenario_keys,
    guidance_scenario_count: debugTrace.value.guidance_refs?.guidance_scenario_count,
    instruction_blocks_used: debugTrace.value.guidance_refs?.instruction_blocks_used,
    identity_fields_used: debugTrace.value.guidance_refs?.identity_fields_used,
    communication_style_mode: debugTrace.value.guidance_refs?.communication_style_mode,
    language_style_modes: debugTrace.value.guidance_refs?.language_style_modes,
    forced_reply_language: debugTrace.value.guidance_refs?.forced_reply_language,
    baseline_compliance_used: debugTrace.value.guidance_refs?.baseline_compliance_used,
    structured_guideline_keys: debugTrace.value.guidance_refs?.structured_guideline_keys,
    structured_guideline_count: debugTrace.value.guidance_refs?.structured_guideline_count,
    answer_length_strategy: debugTrace.value.guidance_refs?.answer_length_strategy,
    directness_strategy: debugTrace.value.guidance_refs?.directness_strategy,
    sensitive_topics_strategy: debugTrace.value.guidance_refs?.sensitive_topics_strategy,
    structured_guardrail_keys: debugTrace.value.guidance_refs?.structured_guardrail_keys,
    structured_guardrail_count: debugTrace.value.guidance_refs?.structured_guardrail_count,
    outside_knowledge_strategy: debugTrace.value.guidance_refs?.outside_knowledge_strategy,
    promise_safety_strategy: debugTrace.value.guidance_refs?.promise_safety_strategy,
    citation_mode: debugTrace.value.guidance_refs?.citation_mode,
    citation_enabled: debugTrace.value.guidance_refs?.citation_enabled,
    guardrails_count: debugTrace.value.guidance_refs?.guardrails_count,
    response_guidelines_count: debugTrace.value.guidance_refs?.response_guidelines_count,
    scenarios_count: debugTrace.value.guidance_refs?.scenarios_count,
  })
);

const procedureRefRows = computed(() =>
  buildKeyValueRows(debugTrace.value.procedure_refs)
);

const procedureExecutionRows = computed(() =>
  buildKeyValueRows(debugTrace.value.procedure_execution)
);

const escalationRefRows = computed(() =>
  buildKeyValueRows(debugTrace.value.escalation_refs)
);

const attributeRefRows = computed(() =>
  buildKeyValueRows(debugTrace.value.attribute_refs)
);

const resolveInboxName = id => {
  const num = Number(id);
  if (!Number.isFinite(num)) return '';
  const inbox = inboxById.value?.(num) || {};
  return inbox?.name || String(num);
};

const reloadContext = async ({ showToast = false } = {}) => {
  if (!props.assistant?.id) return;
  isReloading.value = true;
  try {
    await Promise.all([
      store.dispatch('aiCloser/fetchConfig', {
        assistantId: props.assistant.id,
      }),
      store.dispatch('aiCloser/fetchProducts', {
        assistantId: props.assistant.id,
      }),
      store.dispatch('ailabInboxes/get', {
        assistantId: props.assistant.id,
      }),
      store.dispatch('inboxes/get'),
      loadSavedBatchTestSets(),
    ]);
    await refreshReleaseReadinessSummary();
    if (showToast) alert('Test workspace reloaded');
  } catch (error) {
    alert(error?.message || 'Failed to reload test workspace');
  } finally {
    isReloading.value = false;
  }
};

const savePromptSectionsPatch = async patch => {
  if (!props.assistant?.id) return null;
  return store.dispatch('aiCloser/updateConfig', {
    assistantId: props.assistant.id,
    data: {
      prompt_sections: {
        ...(promptSections.value || {}),
        ...(patch || {}),
      },
    },
  });
};

const saveReleaseLoopConfig = async patch =>
  savePromptSectionsPatch({
    release_loop: {
      ...(currentReleaseLoop.value || {}),
      ...(patch || {}),
    },
  });

const refreshReleaseReadinessSummary = async () => {
  if (!props.assistant?.id) return null;
  const releaseResponse = await AiCloserAPI.getReleaseReadiness(props.assistant.id);
  releaseReadinessSummary.value = {
    ...(releaseResponse?.data?.release_readiness || {}),
    published_version_id: releaseResponse?.data?.published_version_id,
    draft_version_id: releaseResponse?.data?.draft_version_id,
  };
  return releaseReadinessSummary.value;
};

const loadSavedBatchTestSets = async () => {
  if (!props.assistant?.id) return [];
  try {
    const response = await AiCloserAPI.getSavedTestSets(props.assistant.id);
    savedBatchTestSetsData.value = response?.data?.saved_test_sets || [];
  } catch {
    savedBatchTestSetsData.value = [];
  }
  return savedBatchTestSetsData.value;
};

const saveCurrentBatchTestSetToConfigFallback = async title => {
  const nextSets = mergeSavedBatchTestSets({
    sets: savedBatchTestSets.value,
    set: {
      title,
      default_inbox_id: selectedInboxId.value || '',
      updated_at: new Date().toISOString(),
      cases: parsedBatchCases.value.map(item => ({
        inbox_id: item.inboxId ? Number(item.inboxId) : null,
        query: item.query,
      })),
    },
  });
  await saveReleaseLoopConfig({ saved_test_sets: nextSets });
  selectedSavedBatchSetKey.value = nextSets[0]?.set_key || '';
  return nextSets[0] || null;
};

const deleteSavedBatchTestSetFromConfigFallback = async () => {
  const nextSets = savedBatchTestSets.value.filter(
    item => item.set_key !== currentSavedBatchTestSet.value?.set_key
  );
  await saveReleaseLoopConfig({ saved_test_sets: nextSets });
};

const buildBatchBaselinePayload = ({
  rows = [],
  summary = summarizeBatchSimulationRows(rows),
} = {}) => {
  const firstRow = Array.isArray(rows) ? rows[0] || null : null;
  const topBlockedRow = Array.isArray(rows)
    ? rows.find(row => row.level === 'blocked') || null
    : null;
  const topWatchRow = Array.isArray(rows)
    ? rows.find(row => row.level === 'watch') || null
    : null;
  const setKey =
    currentSavedBatchTestSet.value?.set_key || selectedSavedBatchSetKey.value || '';
  const title =
    String(
      currentSavedBatchTestSet.value?.title ||
        batchSetTitleDraft.value ||
        'Ad hoc batch baseline'
    ).trim() || 'Ad hoc batch baseline';

  return {
    baseline_key: setKey || title,
    set_key: setKey,
    title,
    source: 'Test',
    overall_level: summary.overallLevel,
    confidence_level: summary.overallLevel,
    confidence_runs: summary.total,
    confidence_title:
      summary.blocked > 0
        ? 'Batch run found release blockers'
        : summary.watch > 0
          ? 'Batch run still needs rollout review'
          : summary.total > 0
            ? 'Batch run cleared release checks'
            : 'No batch evidence yet',
    total_cases: summary.total,
    ready_cases: summary.ready,
    watch_cases: summary.watch,
    blocked_cases: summary.blocked,
    primary_inbox_id:
      Number(
        firstRow?.inboxId ||
          selectedInboxId.value ||
          seededTestContext.value.inboxId ||
          0
      ) || null,
    top_blocker: String(topBlockedRow?.blockers?.[0] || '').trim(),
    top_watch_signal: String(topWatchRow?.watchSignals?.[0] || '').trim(),
    ran_at: new Date().toISOString(),
    ...(seededRepairBundle.value.enabled
      ? {
          bundle_title: seededRepairBundle.value.title || '',
          focus_label: seededRepairBundle.value.focusLabel || '',
          strategy_group_title: seededRepairBundle.value.strategyGroupTitle || '',
          recommendation_code: seededRepairBundle.value.recommendationCode || '',
          owner_workspace: seededRepairBundle.value.ownerWorkspace || '',
          owner_action_title: seededRepairBundle.value.ownerActionTitle || '',
          guidance_editor: seededRepairBundle.value.guidanceEditor || '',
          escalation_focus: seededRepairBundle.value.escalationFocus || '',
        }
      : {}),
  };
};

const persistBatchBaseline = async ({ rows = [] } = {}) => {
  const summary = summarizeBatchSimulationRows(rows);
  const payload = buildBatchBaselinePayload({ rows, summary });
  try {
    const existing = findBatchBaseline({
      baselines: currentBatchBaselines.value,
      baselineKey: payload.baseline_key,
      setKey: payload.set_key,
      bundle: seededRepairBundle.value.enabled ? seededRepairBundle.value : {},
    });
    if (existing?.id) {
      await AiCloserAPI.updateBatchBaseline(props.assistant.id, existing.id, payload);
    } else {
      await AiCloserAPI.createBatchBaseline(props.assistant.id, payload);
    }
    await refreshReleaseReadinessSummary();
    return findBatchBaseline({
      baselines: releaseReadinessSummary.value?.batch_baselines || [],
      baselineKey: payload.baseline_key,
      setKey: payload.set_key,
      bundle: seededRepairBundle.value.enabled ? seededRepairBundle.value : {},
    });
  } catch {
    const nextBaselines = mergeBatchBaselines({
      baselines: currentReleaseLoop.value?.batch_baselines || [],
      baseline: payload,
    });
    await saveReleaseLoopConfig({
      batch_baselines: nextBaselines,
    });
    await refreshReleaseReadinessSummary();
    return nextBaselines[0] || null;
  }
};

const simulateQueryRequest = async ({ queryText = '', inboxId = '' } = {}) => {
  const res = await AiCloserAPI.simulateConfig(props.assistant.id, {
    query: String(queryText || '').trim(),
    inboxId: inboxId ? Number(inboxId) : null,
    includePrompt: false,
    includeResponse: true,
    estimatedCompletionTokens: 250,
  });
  return res?.data || null;
};

const runSimulation = async () => {
  if (!props.assistant?.id) return;
  if (!query.value.trim()) {
    alert('Query is required');
    return;
  }
  isSimulating.value = true;
  try {
    const resultData = await simulateQueryRequest({
      queryText: query.value.trim(),
      inboxId: selectedInboxId.value,
    });
    simulateResult.value = resultData;
    if (seededRepairBundle.value.enabled && resultData) {
      const evaluation = evaluateRepairBundleResult(resultData, seededRepairBundle.value);
      if (evaluation) {
        repairValidationRuns.value = saveRepairBundleRun({
          assistantId: props.assistant?.id,
          bundle: seededRepairBundle.value,
          run: {
            ranAt: new Date().toISOString(),
            status: evaluation.status,
            tone: evaluation.tone,
            deliveryMode: evaluation.deliveryMode || '',
            sourceKind: evaluation.sourceKind || '',
            contentGapReason: evaluation.contentGapReason || '',
            scopeWarning: evaluation.scopeWarning || '',
          },
        });
      }
    }
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Simulation failed');
  } finally {
    isSimulating.value = false;
  }
};

onMounted(async () => {
  applySeededContext();
  if (seededRepairBundle.value.enabled) {
    repairValidationRuns.value = loadRepairBundleRuns({
      assistantId: props.assistant?.id,
      bundle: seededRepairBundle.value,
    });
  }
  await reloadContext();
});

watch(
  () => selectedSavedBatchSetKey.value,
  setKey => {
    const row = findSavedBatchTestSet({
      sets: savedBatchTestSets.value,
      setKey,
    });
    if (row) batchSetTitleDraft.value = row.title || '';
  }
);

watch(
  () => savedBatchTestSets.value.map(item => item.set_key).join('|'),
  () => {
    if (
      selectedSavedBatchSetKey.value &&
      !findSavedBatchTestSet({
        sets: savedBatchTestSets.value,
        setKey: selectedSavedBatchSetKey.value,
      })
    ) {
      selectedSavedBatchSetKey.value = '';
    }
  },
  { immediate: true }
);

watch(
  () => [
    route.query?.test_query,
    route.query?.test_inbox_id,
    route.query?.test_reason,
    route.query?.test_source,
    route.query?.test_compare_bundle,
    route.query?.test_compare_title,
    route.query?.test_compare_stage,
  ],
  () => {
    applySeededContext();
    simulateResult.value = null;
    repairValidationRuns.value = seededRepairBundle.value.enabled
      ? loadRepairBundleRuns({
          assistantId: props.assistant?.id,
          bundle: seededRepairBundle.value,
        })
      : [];
  },
  { immediate: true }
);
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div class="fin-page-header-row test-header">
      <div>
        <h1 class="heading o__size-1">{{ pageTitle }}</h1>
        <p class="test-muted mt-1">{{ pageDescription }}</p>
      </div>
      <button type="button" class="btn o__secondary" :disabled="isReloading" @click="reloadContext({ showToast: true })">
        {{ isReloading ? 'Reloading...' : 'Reload context' }}
      </button>
    </div>

    <div class="fin-page-body test-shell">
      <div class="test-summary-grid">
        <div v-for="card in summaryCards" :key="card.label" class="test-stat-card">
          <div class="test-label">{{ card.label }}</div>
          <div class="test-value">{{ card.value }}</div>
        </div>
      </div>

      <div v-if="seededContextCard" class="test-card">
        <div class="test-card-title">{{ seededContextCard.title }}</div>
        <div class="test-muted mt-1">{{ seededContextCard.body }}</div>
        <div class="test-muted mt-2">
          Query: <strong>{{ seededTestContext.query }}</strong>
          <span v-if="seededTestContext.inboxId">
            • Inbox {{ resolveInboxName(seededTestContext.inboxId) }}
          </span>
        </div>
        <div class="test-actions">
          <button type="button" class="btn o__secondary" @click="clearSeededContext">
            Clear seeded context
          </button>
        </div>
      </div>

      <div v-if="rollbackWritebackCard" class="test-card">
        <div class="test-card-title">{{ rollbackWritebackCard.title }}</div>
        <div class="test-muted mt-1">{{ rollbackWritebackCard.body }}</div>
        <div
          v-if="rollbackEvidenceHistoryEntries.length"
          class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
        >
          <div class="font-medium text-slate-700">Recent evidence notes</div>
          <div
            v-if="rollbackEvidenceDrilldownBadges.length"
            class="mt-2 flex flex-wrap gap-2"
          >
            <span
              v-for="badge in rollbackEvidenceDrilldownBadges"
              :key="badge.label"
              class="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
              :class="{ 'bg-emerald-100 text-emerald-800': badge.tone === 'primary' }"
            >
              {{ badge.label }}
            </span>
          </div>
          <div
            v-for="(entry, index) in rollbackEvidenceHistoryEntries"
            :key="rollbackEvidenceHistoryEntryKey(entry, index)"
            :class="index > 0 ? 'mt-2' : 'mt-2'"
          >
            <div>{{ entry.body }}</div>
            <div
              v-if="rollbackEvidenceHistoryEntryBadges(entry).length"
              class="mt-2 flex flex-wrap gap-2"
            >
              <span
                v-for="badge in rollbackEvidenceHistoryEntryBadges(entry)"
                :key="badge.label"
                class="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
                :class="{ 'bg-emerald-100 text-emerald-800': badge.tone === 'primary' }"
              >
                {{ badge.label }}
              </span>
            </div>
            <div
              v-if="rollbackEvidenceHistoryEntryMeta(entry)"
              class="test-muted mt-1"
            >
              {{ rollbackEvidenceHistoryEntryMeta(entry) }}
            </div>
          </div>
          <div
            v-if="rollbackEvidenceHistoryTotalCount > rollbackEvidenceHistoryEntries.length"
            class="test-muted mt-2"
          >
            {{
              `${rollbackEvidenceHistoryTotalCount - rollbackEvidenceHistoryEntries.length} older rollback note${rollbackEvidenceHistoryTotalCount - rollbackEvidenceHistoryEntries.length === 1 ? '' : 's'} remain in linked evidence`
            }}
          </div>
        </div>
        <div
          v-if="releaseReadinessSummary.latest_rollback_outcome?.summary"
          class="test-actions mt-3"
        >
          <button
            type="button"
            class="btn o__secondary"
            @click="openDeployRollbackEvidence()"
          >
            Open evidence in Deploy
          </button>
          <button
            type="button"
            class="btn o__secondary"
            @click="openDeployRollbackEvidence({ openVersions: true })"
          >
            Open versions in Deploy
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="isPersistingRollbackFollowup"
            @click="persistRollbackFollowupToEvidence"
          >
            {{
              isPersistingRollbackFollowup
                ? 'Saving rollback note...'
                : 'Append rollback note to evidence'
            }}
          </button>
          <button type="button" class="btn o__secondary" @click="openDeployRollbackFollowup">
            Write rollback follow-up to Deploy
          </button>
        </div>
      </div>

      <div v-if="seededRepairBundle.enabled" class="test-card">
        <div class="test-card-title">
          {{ seededRepairBundle.title || 'Repair bundle compare loop' }}
        </div>
        <div class="test-muted mt-1">
          Compare the current run against the seeded hotspot, a cleaner resolved
          example, and an escalated example before you validate the repair.
        </div>
        <div
          v-if="seededRepairBundle.focusLabel || seededRepairBundle.strategyGroupTitle"
          class="test-muted mt-2"
        >
          <span v-if="seededRepairBundle.focusLabel">
            Focus: <strong>{{ seededRepairBundle.focusLabel }}</strong>
          </span>
          <span v-if="seededRepairBundle.strategyGroupTitle">
            • {{ seededRepairBundle.strategyGroupTitle }}
          </span>
        </div>
        <div
          v-if="repairBundleVerdict"
          class="test-verdict-card mt-3"
          :class="`tone-${repairBundleVerdict.tone}`"
        >
          <div class="test-label">Validation verdict</div>
          <div class="test-card-title">{{ repairBundleVerdict.title }}</div>
          <div class="test-muted mt-1">{{ repairBundleVerdict.body }}</div>
          <div v-if="repairBundleVerdict.nextStep" class="test-muted mt-2">
            Next: {{ repairBundleVerdict.nextStep }}
          </div>
          <div
            v-if="repairBundleVerdictSignals.length"
            class="test-checklist-list mt-3"
          >
            <div
              v-for="signal in repairBundleVerdictSignals"
              :key="signal"
              class="test-checklist-row"
            >
              <strong>Signal</strong>
              <span>{{ signal }}</span>
            </div>
          </div>
          <div
            v-if="repairBundleValidationChecklist.length"
            class="test-checklist-list mt-3"
          >
            <div
              v-for="item in repairBundleValidationChecklist"
              :key="item.label"
              class="test-checklist-row"
            >
              <strong>{{ item.passed ? 'Pass' : 'Check' }}</strong>
              <span>{{ item.label }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="repairBundleConfidence"
          class="test-verdict-card mt-3"
          :class="`tone-${repairBundleConfidence.tone}`"
        >
          <div class="test-label">Repair confidence</div>
          <div class="test-card-title">{{ repairBundleConfidence.title }}</div>
          <div class="test-muted mt-1">{{ repairBundleConfidence.body }}</div>
          <div v-if="repairBundleConfidence.nextStep" class="test-muted mt-2">
            Next: {{ repairBundleConfidence.nextStep }}
          </div>
          <div
            v-if="repairValidationRunRows.length"
            class="test-checklist-list mt-3"
          >
            <div
              v-for="run in repairValidationRunRows"
              :key="`${run.ranAt}-${run.label}`"
              class="test-checklist-row"
            >
              <strong>
                {{ run.label }}
                <span v-if="run.timeLabel"> {{ run.timeLabel }}</span>
              </strong>
              <span>
                {{ formatTraceLabel(run.status) }}
                <span v-if="run.deliveryMode"> • delivery {{ run.deliveryMode }}</span>
                <span v-if="run.sourceKind"> • source {{ run.sourceKind }}</span>
                <span v-if="run.contentGapReason">
                  • gap {{ run.contentGapReason }}
                </span>
                <span v-else> • gap clear</span>
                <span v-if="run.scopeWarning">
                  • scope {{ run.scopeWarning }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="repairBundleReleaseReview"
          class="test-verdict-card mt-3"
          :class="`tone-${repairBundleReleaseReview.status === 'rolled_back' ? 'warning' : 'info'}`"
        >
          <div class="test-label">Durable release review</div>
          <div class="test-card-title">
            {{
              repairBundleReleaseReview.status === 'released'
                ? 'Marked released'
                : repairBundleReleaseReview.status === 'rolled_back'
                  ? 'Rollback marked'
                  : 'Release snapshot saved'
            }}
          </div>
          <div class="test-muted mt-1">
            <span v-if="repairBundleReleaseReview.reviewed_at">
              {{ formatValidationRunTime(repairBundleReleaseReview.reviewed_at) }}
            </span>
            <span v-if="repairBundleReleaseReview.confidence_title">
              • {{ repairBundleReleaseReview.confidence_title }}
            </span>
            <span v-if="repairBundleReleaseReview.rollback_trigger">
              • rollback {{ repairBundleReleaseReview.rollback_trigger }}
            </span>
          </div>
          <div v-if="repairBundleReleaseReview.note" class="test-muted mt-2">
            {{ repairBundleReleaseReview.note }}
          </div>
        </div>
        <div class="test-compare-grid mt-3">
          <div
            v-for="card in repairBundleCompareCards"
            :key="card.key"
            class="test-compare-card"
            :class="{ 'test-compare-card--active': card.active }"
          >
            <div class="test-label">
              {{ card.label }}
              <span v-if="card.active">• current focus</span>
            </div>
            <div v-if="card.body" class="test-muted">{{ card.body }}</div>
            <div v-if="card.extra" class="test-muted mt-2">{{ card.extra }}</div>
          </div>
        </div>
        <div class="test-actions">
          <button
            v-if="repairBundleOwnerAction"
            type="button"
            class="btn o__secondary"
            @click="openTestAction(repairBundleOwnerAction)"
          >
            {{ repairBundleOwnerAction.actionLabel }}
          </button>
          <button
            type="button"
            class="btn o__secondary"
            @click="openAnalyzeWithCurrentContext"
          >
            Open Analyze with this context
          </button>
        </div>
      </div>

      <div class="test-card">
        <div class="test-card-title">Runtime strategy snapshot</div>
        <div class="test-muted mb-3">
          This is the current real configuration that the simulation will use.
        </div>
        <div class="test-summary-grid">
          <div v-for="card in runtimeStrategyCards" :key="card.label" class="test-stat-card">
            <div class="test-label">{{ card.label }}</div>
            <div class="test-value">{{ card.value }}</div>
            <div class="test-muted mt-1">{{ card.note }}</div>
          </div>
        </div>
      </div>

      <div class="test-card">
        <div class="test-card-title">What to change next</div>
        <div class="test-muted mb-3">
          After each simulation, jump directly into the page that most likely owns
          the fix.
        </div>
        <div class="test-next-action-list">
          <div v-for="card in testNextActionCards" :key="card.title" class="test-next-action-card">
            <div>
              <div class="test-card-title">{{ card.title }}</div>
              <div class="test-muted mt-1">{{ card.body }}</div>
            </div>
            <div class="test-actions test-actions--inline">
              <button
                type="button"
                class="btn o__secondary"
                @click="openTestAction(card)"
              >
                {{ card.actionLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="test-card">
        <div class="test-card-title">Release checks from this simulation</div>
        <div class="test-muted mb-3">
          Before shipping, confirm the runtime is active, the right channels are
          connected, and the delivery path matches what you expect.
        </div>
        <div class="test-summary-grid">
          <div v-for="card in releaseCheckCards" :key="card.label" class="test-stat-card">
            <div class="test-label">{{ card.label }}</div>
            <div class="test-value">{{ card.value }}</div>
            <div class="test-muted mt-1">{{ card.note }}</div>
          </div>
        </div>
      </div>

      <div class="test-card">
        <div class="test-card-title">Trace warnings</div>
        <div class="test-muted mb-3">
          Use these signals to decide whether the next fix belongs in Content,
          Guidance, Escalation, or Deploy.
        </div>
        <div class="test-next-action-list">
          <div
            v-for="card in traceWarningCards"
            :key="card.title"
            class="test-next-action-card"
          >
            <div>
              <div class="test-card-title">{{ card.title }}</div>
              <div class="test-muted mt-1">{{ card.body }}</div>
            </div>
            <div class="test-actions test-actions--inline">
              <button
                type="button"
                class="btn o__secondary"
                @click="openTestAction(card)"
              >
                {{ card.actionLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="test-card">
        <input
          ref="savedTestSetImportInputRef"
          type="file"
          accept="application/json,.json"
          class="hidden"
          @change="handleSavedBatchTestSetImport"
        />
        <div class="test-grid">
          <label class="test-field">
            <span class="test-label">Inbox</span>
            <select v-model="selectedInboxId" class="test-input">
              <option value="">All inboxes</option>
              <option v-for="ci in connectedInboxes" :key="ci.id" :value="ci.id">
                {{ ci.name || resolveInboxName(ci.id) }}
              </option>
            </select>
          </label>
        </div>

        <label class="test-field mt-4">
          <span class="test-label">Query</span>
          <textarea v-model="query" rows="4" class="test-input test-textarea" placeholder="Type the customer message you want to test." />
        </label>
        <div class="test-grid mt-4">
          <label class="test-field">
            <span class="test-label">Saved batch set name</span>
            <input
              v-model="batchSetTitleDraft"
              type="text"
              class="test-input"
              placeholder="Release readiness baseline"
            />
          </label>
          <label class="test-field">
            <span class="test-label">Saved batch sets</span>
            <select v-model="selectedSavedBatchSetKey" class="test-input">
              <option value="">Select a saved set</option>
              <option
                v-for="item in savedBatchTestSets"
                :key="item.set_key"
                :value="item.set_key"
              >
                {{ item.title }} ({{ item.case_count }} cases)
              </option>
            </select>
          </label>
        </div>
        <label class="test-field mt-4">
          <span class="test-label">Batch test set</span>
          <textarea
            v-model="batchInput"
            rows="6"
            class="test-input test-textarea"
            placeholder="Paste one case per line. Optional format: inboxId | customer question"
          />
          <span class="test-muted mt-1">
            Parsed cases: {{ parsedBatchCases.length }}.
            Use `123 | question` to override the selected inbox for a specific line.
          </span>
          <span class="test-muted mt-1">
            Saved sets persist inside this config so Deploy and the learning loop keep the same release baseline.
          </span>
        </label>

        <div class="test-actions">
          <button type="button" class="btn o__secondary" :disabled="isSimulating" @click="runSimulation">
            {{ isSimulating ? 'Running...' : 'Simulate response' }}
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="isRunningBatch || !parsedBatchCases.length"
            @click="runBatchSimulation"
          >
            {{ isRunningBatch ? 'Running batch...' : 'Run batch set' }}
          </button>
          <button type="button" class="btn o__secondary" @click="prefillBatchFromCurrentQuery">
            Add current query to batch
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="!parsedBatchCases.length"
            @click="saveCurrentBatchTestSet"
          >
            Save batch set
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="!currentSavedBatchTestSet"
            @click="loadSavedBatchTestSet"
          >
            Load saved set
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="!currentSavedBatchTestSet"
            @click="deleteSavedBatchTestSet"
          >
            Delete saved set
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="isExportingSavedSets || !savedBatchTestSets.length"
            @click="exportSavedBatchTestSets"
          >
            {{ isExportingSavedSets ? 'Exporting...' : 'Export library' }}
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="isImportingSavedSets"
            @click="openSavedBatchTestSetImport"
          >
            {{ isImportingSavedSets ? 'Importing...' : 'Import library' }}
          </button>
          <button
            type="button"
            class="btn o__secondary"
            :disabled="!batchSimulationRows.length"
            @click="clearBatchResults"
          >
            Clear batch results
          </button>
          <button type="button" class="btn o__secondary" @click="openAnalyzeWithCurrentContext">
            Open Analyze with this context
          </button>
        </div>
      </div>

      <div class="test-card">
        <div class="test-card-title">Batch simulation set</div>
        <div class="test-muted mb-3">
          Run multiple customer questions through the same runtime so you can judge release readiness as a set, not only one trace at a time.
        </div>
        <div class="test-summary-grid">
          <div v-for="card in batchSummaryCards" :key="card.label" class="test-stat-card">
            <div class="test-label">{{ card.label }}</div>
            <div class="test-value">{{ card.value }}</div>
            <div class="test-muted mt-1">{{ card.note }}</div>
          </div>
        </div>
        <div v-if="batchNextActionCards.length" class="test-next-action-list mt-4">
          <div
            v-for="card in batchNextActionCards"
            :key="card.title"
            class="test-next-action-card"
          >
            <div>
              <div class="test-card-title">{{ card.title }}</div>
              <div class="test-muted mt-1">{{ card.body }}</div>
            </div>
            <div class="test-actions test-actions--inline">
              <button
                type="button"
                class="btn o__secondary"
                @click="openTestAction(card)"
              >
                {{ card.actionLabel }}
              </button>
            </div>
          </div>
        </div>
        <div v-if="batchSimulationRows.length" class="test-next-action-list mt-4">
          <div
            v-for="row in batchSimulationRows"
            :key="row.id"
            class="test-next-action-card"
          >
            <div>
              <div class="test-card-title">
                {{ row.label }}
                <span class="test-muted">
                  • {{ row.level }}
                  <span v-if="row.inboxId">
                    • {{ resolveInboxName(row.inboxId) }}
                  </span>
                </span>
              </div>
              <div class="test-muted mt-1">
                Delivery {{ row.deliveryMode || 'unknown' }} • messages {{ row.messageCount }}
                <span v-if="row.contentGapReason"> • gap {{ row.contentGapReason }}</span>
                <span v-if="row.scopeWarning"> • scope {{ row.scopeWarning }}</span>
              </div>
              <div
                v-if="row.blockers?.length || row.watchSignals?.length"
                class="test-muted mt-1"
              >
                {{ [...(row.blockers || []), ...(row.watchSignals || [])].join(' • ') }}
              </div>
            </div>
            <div class="test-actions test-actions--inline">
              <button
                type="button"
                class="btn o__secondary"
                @click="
                  openTestAction({
                    action: 'analyze_batch_row',
                    actionLabel: 'Open Analyze',
                    batchRowId: row.id,
                  })
                "
              >
                Open Analyze
              </button>
            </div>
          </div>
        </div>
        <div v-else class="test-empty mt-4">
          No batch results yet. Paste multiple cases above and run the set.
        </div>
      </div>

      <div class="test-two-col">
        <div class="test-card">
          <div class="test-card-title">Simulation output</div>
          <div class="test-muted mb-3">Final response bubbles generated by the runtime.</div>
          <div v-if="simulationMessages.length" class="test-message-list">
            <div v-for="(message, index) in simulationMessages" :key="index" class="test-message">
              {{ message }}
            </div>
          </div>
          <div v-else class="test-empty">No simulation output yet.</div>
        </div>
      </div>

      <div class="test-two-col">
        <div class="test-card">
          <div class="test-card-title">Decision trace</div>
          <div class="test-muted mb-3">The key answer path returned by the runtime.</div>
          <div class="test-trace-grid">
            <div v-for="row in traceRows" :key="row[0]" class="test-trace-row">
              <span class="test-label">{{ row[0] }}</span>
              <span>{{ row[1] }}</span>
            </div>
          </div>
          <div v-if="contextCountRows.length" class="mt-4">
            <div class="test-label mb-2">Context counts</div>
            <div class="test-trace-grid">
              <div v-for="row in contextCountRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span>{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="contextRefRows.length" class="mt-4">
            <div class="test-label mb-2">Context refs</div>
            <div class="test-trace-grid">
              <div v-for="row in contextRefRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="retrievalRefRows.length" class="mt-4">
            <div class="test-label mb-2">Retrieval strategy</div>
            <div class="test-trace-grid">
              <div v-for="row in retrievalRefRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="guidanceRefRows.length" class="mt-4">
            <div class="test-label mb-2">Guidance attribution</div>
            <div class="test-trace-grid">
              <div v-for="row in guidanceRefRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="procedureRefRows.length" class="mt-4">
            <div class="test-label mb-2">Procedure refs</div>
            <div class="test-trace-grid">
              <div v-for="row in procedureRefRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="procedureExecutionRows.length" class="mt-4">
            <div class="test-label mb-2">Procedure execution</div>
            <div class="test-trace-grid">
              <div v-for="row in procedureExecutionRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="escalationRefRows.length" class="mt-4">
            <div class="test-label mb-2">Escalation refs</div>
            <div class="test-trace-grid">
              <div v-for="row in escalationRefRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
          <div v-if="attributeRefRows.length" class="mt-4">
            <div class="test-label mb-2">Attribute refs</div>
            <div class="test-trace-grid">
              <div v-for="row in attributeRefRows" :key="row[0]" class="test-trace-row">
                <span class="test-label">{{ row[0] }}</span>
                <span class="test-trace-value">{{ row[1] }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.test-shell { display: flex; flex-direction: column; gap: 16px; min-height: 0; }
.test-header { align-items: flex-start; }
.test-summary-grid, .test-mini-grid, .test-trace-grid { display: grid; gap: 12px; }
.test-summary-grid { grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); }
.test-compare-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.test-mini-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.test-two-col { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.test-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.test-card, .test-stat-card, .test-mini-card, .test-trace-row, .test-next-action-card { border: 1px solid var(--fin-neutral-border); background: #fff; border-radius: 14px; }
.test-card { padding: 16px; }
.test-verdict-card { border: 1px solid #dbe4f0; border-radius: 12px; padding: 12px; background: #f8fbff; }
.test-verdict-card.tone-success { background: #ecfdf5; border-color: #86efac; }
.test-verdict-card.tone-warning { background: #fffbeb; border-color: #fcd34d; }
.test-verdict-card.tone-danger { background: #fef2f2; border-color: #fca5a5; }
.test-verdict-card.tone-info { background: #eff6ff; border-color: #93c5fd; }
.test-compare-card { border: 1px solid #dbe4f0; background: #f8fbff; border-radius: 12px; padding: 12px; }
.test-compare-card--active { border-color: #93c5fd; background: #eff6ff; box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.12); }
.test-stat-card, .test-mini-card, .test-trace-row { padding: 12px; }
.test-card-title { font-size: 14px; line-height: 19px; font-weight: 650; color: #111827; }
.test-label { font-size: 11px; line-height: 15px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 4px; }
.test-value { font-size: 17px; line-height: 22px; font-weight: 700; color: #111827; }
.test-trace-value { word-break: break-word; }
.test-muted { color: #6b7280; font-size: 12px; line-height: 17px; }
.test-field { display: flex; flex-direction: column; gap: 6px; }
.test-next-action-list { display: flex; flex-direction: column; gap: 10px; }
.test-next-action-card { padding: 12px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.test-actions--inline { justify-content: flex-start; margin-top: 0; }
.test-field--wide { grid-column: span 2; }
.test-input { width: 100%; border: 1px solid #d1d5db; border-radius: 10px; padding: 9px 11px; font-size: 13px; line-height: 18px; background: #fff; color: #111827; }
.test-textarea { resize: vertical; }
.test-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.test-checklist-list { display: flex; flex-direction: column; gap: 8px; }
.test-checklist-row { display: flex; gap: 10px; align-items: flex-start; color: #111827; font-size: 12px; line-height: 17px; }
.test-checklist-row strong { min-width: 42px; color: #374151; font-size: 11px; text-transform: uppercase; letter-spacing: 0.02em; }
.test-pre { margin: 0; margin-top: 12px; padding: 12px; border-radius: 12px; background: #f8fafc; color: #111827; font-size: 11px; line-height: 17px; white-space: pre-wrap; overflow-x: auto; max-height: 420px; overflow-y: auto; }
.test-message-list { display: flex; flex-direction: column; gap: 10px; }
.test-message { border-radius: 12px; background: #f8fafc; padding: 11px 12px; color: #111827; font-size: 13px; line-height: 18px; }
.test-empty { border: 1px dashed #d1d5db; border-radius: 12px; padding: 16px; color: #6b7280; font-size: 13px; text-align: center; }
.test-table-wrap { overflow-x: auto; }
.test-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.test-table th, .test-table td { text-align: left; padding: 9px 11px; border-top: 1px solid #e5e7eb; color: #111827; }
@media (max-width: 960px) { .test-two-col, .test-grid, .test-compare-grid { grid-template-columns: 1fr; } .test-field--wide { grid-column: auto; } }
</style>
